import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal, flushSync } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import MediaPreview from '../../components/media-preview/MediaPreview';
import TopAppBarHome, {
  TopAppBarFloat,
  TOP_APP_BAR_HOME_HEIGHT,
} from '../../components/top-app-bar/TopAppBarHome';
import TakeLabel from '../../components/label/TakeLabel';
import IconButton from '../../components/button/IconButton';
import ToolTip from '../../components/tooltip/ToolTip';
import CommentField, { ExistingAttachedFile, AiAssistSendMeta, CommentFieldHandle } from '../../components/comment-field/CommentField';
import MessageItem from '../../components/message/MessageItem';
import SvgLoadingAnimation from '../../components/loading';
import Modal from '../../components/modal/Modal';
import ConfirmModal from '../../components/modal/ConfirmModal';
import UploadProgressBar from '../../components/upload-progress/UploadProgressBar';
import { useUploadFailureDialog } from '../../components/upload-progress/useUploadFailureDialog';
import DropZoneOverlay from '../../components/drop-zone-overlay/DropZoneOverlay';
import DateDisplay, { DATE_DISPLAY_TYPE } from '../../components/date-display/DateDisplay';
import { CircularFab } from '../../components/fab';
import {
  setSpacePlaybackTarget,
  clearSpacePlaybackTarget,
  toggleSpacePlaybackTarget,
} from '../../components/audio-preview';

import {
  useGetMediaItemsQuery,
  useCreateMediaItemMutation,
  useUpdateMediaItemSilentMutation,
  useUpdateMediaItemTagsMutation,
  mediaItemsApi,
  MediaItem,
  MediaItemProducerTag,
  MediaItemClientTag,
  MediaTagSide,
} from '../../app/services/mediaItems';
import {
  useGetShotCommentsQuery,
  useLazyGetShotCommentsQuery,
  useLazyGetShotCommentDetailQuery,
  useSendShotCommentMutation,
  useUpdateShotCommentMutation,
  useDeleteShotCommentMutation,
  useMarkCommentFilesAsDownloadedMutation,
  useMarkShotCommentsAsReadMutation,
  ShotComment,
  ShotCommentUser,
  SendShotCommentResponse,
} from '../../app/services/shotComments';
import TypingIndicator from '../../components/typing-indicator/TypingIndicator';
import {
  useMessengerReconnect,
  useMessengerSocket,
  useMessengerSubscribe,
} from '../../app/messenger/MessengerSocketContext';
import { useTypingSender } from '../../hooks/useTypingSender';
import { useBatchedCall } from '../../hooks/useBatchedCall';
import type { MessengerIncomingEvent } from '../../types/messengerEvents';
import { extractDetailEntity } from '../../utils/detailResponse';
import { useSyncUserDirectory } from '../../hooks/useSyncUserDirectory';
import { collectMessengerUsers } from '../../utils/collectMessengerUsers';
import { useUpdateShotStatusMutation, useGetShotDetailQuery } from '../../app/services/shots';
import { useGetProjectQuery } from '../../app/services/projects';
import { useGetProjectMembershipQuery } from '../../app/accounts/memberships';
import { useGetCurrentUserQuery } from '../../app/accounts/authuser';
import ProjectAmbientBackground from '../../components/project/ProjectAmbientBackground';
import { getProjectAmbient } from '../../utils/projectAmbient';
// @ts-ignore
import { SHOT_STATUS } from '../../constant/shotStatus';
// @ts-ignore
import { setAudioEnabled } from '../../app/actions/audio/audioSlice';

import { shotCommentToMessageData } from '../../utils/messageAdapters';
import {
  isMessageOnRightSide,
  positionGroupOf,
} from '../../utils/memberPositionUtils';
import {
  startSharedMediaReturnTransition,
  PREVIEW_MEDIA_LANDING_ATTR,
  PREVIEW_MEDIA_FLIP_ATTR,
  captureMediaVisualRect,
  resolveMediaFlipBox,
  runMediaLayoutFlip,
} from '../../utils/sharedMediaTransition';
import { extractS3KeyFromUrl } from '../../utils/s3';
import { normalizeNfc } from '../../utils/text';
import { buildShotCommentLink, toMarkdownLink } from '../../utils/deepLink';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { MessageData } from '../../types/message';
import { MediaPreviewHandle, MenuItem, SizeMode } from '../../types/media-preview';
import useShotApproval from '../../hooks/useShotApproval';
import useS3Upload, { UploadingFile, ValidationError, FileWithPath } from '../../hooks/useS3Upload';
import { projectsApi } from '../../app/services/projects';

import { motion, LayoutGroup, AnimatePresence } from 'framer-motion';
import { useAnimatedMessageResolve } from '../../hooks/useAnimatedMessageResolve';
import { useObservedHeight } from '../../hooks/useObservedHeight';
import ShotEditModal from './components/ShotEditModal';
import SortableSidebarThumb, { StaticSidebarThumb } from './components/SortableSidebarThumb';
import { getMediaUrl, toMediaType, isAudioOrVideoType, getMediaFps } from './previewMediaUtils';
import ComparisonSlide from './components/ComparisonSlide';
import ClientTagChips from './components/ClientTagChips';
import TimelinePanel, { TimelinePin } from './components/TimelinePanel';
import { useWaveformPeaks } from './hooks/useWaveformPeaks';
import { useShotCommentDeepLink } from './hooks/useShotCommentDeepLink';
import { usePreviewQueueShortcut } from './hooks/usePreviewQueueShortcut';
import toast from 'react-hot-toast';

import MaSlide from './ma/MaSlide';
import MaExportModal from './ma/MaExportModal';
import { MaOptionSlotChip, isSelectableOptionSlot } from './ma/MaOptionSlotChip';
import { applyOptionSelectionsToSegment } from './ma/linkMaSequence';
import { MaSegment, maSegmentDisplaySeconds, stripExtension } from './ma/maTypes';
import { useMaSequence } from './ma/useMaSequence';
import { useMaTimelineEdit } from './ma/useMaTimelineEdit';
import { useMaTimelineTracks } from './ma/useMaTimelineTracks';
import {
  parseTimecode,
  formatBracketTimecode,
  quantizeToFrame,
  extractFirstTimecode,
  hasTimecode,
  BRACKET_TAG_RE,
} from '../../utils/timecode';

// ===== 型定義 =====
type FilterMode = "in_progress" | "completed" | "search";

/** バリエーショングループ: 同一 variation_name の MediaItem をグループ化 */
type VariationGroup = {
  variationName: string;
  takes: MediaItem[];      // updated_at 降順（最新が先頭）
  latestTake: MediaItem;
  variationOrder: number;
};

/** 比較グループのエントリ（_a / _b .. のサフィックス1文字 = 1エントリ） */
type ComparisonEntry = {
  label: string; // サフィックス文字の大文字（A / B / C ...）
  group: VariationGroup;
};

/**
 * プレビュースライド: 中央カラム / SP スクローラーの1縦スライドに対応。
 * - single: 従来どおりの1バリエーション表示
 * - comparison: `_a` / `_b` .. サフィックスの比較グループ表示
 * - ma: MA シーケンス CSV の動画 1 行分（紐づくオーディオを FE ミックス再生）
 */
type PreviewSlide =
  | { kind: 'single'; group: VariationGroup }
  | { kind: 'comparison'; baseName: string; entries: ComparisonEntry[] }
  | { kind: 'ma'; segment: MaSegment; segmentIndex: number };

// ===== 定数 =====
const DESKTOP_BREAKPOINT = 768;
const LEFT_SIDEBAR_WIDTH = 80;

// 左サイドバーのサムネイルサイズ計算用
const SIDEBAR_PADDING_TOP = 8;
const SIDEBAR_PADDING_BOTTOM = 16;
const SIDEBAR_THUMB_DEFAULT = 56;
const SIDEBAR_THUMB_MIN = 24;
const SIDEBAR_THUMB_GAP = 4;
const MICRO_INTERACTION_DURATION = 1500;
const FADE_OUT_DURATION = 700;

// デスクトップのレイアウト比率（メディア : コメント）— PREVIEW_MEDIA_RATIO を変更するだけで全体に反映。
// SP はコメントをオーバーレイで出す 1 カラムのため、この比率も最大幅も使わない。
const PREVIEW_MEDIA_RATIO = 0.5;
const DESKTOP_MEDIA_MAX_WIDTH = `${PREVIEW_MEDIA_RATIO * 100}vw`;
const COMMENT_PANEL_RATIO = 1 - PREVIEW_MEDIA_RATIO;
const MIN_RIGHT_PANEL = 280;
const MAX_RIGHT_PANEL_RATIO = 0.5; // コメントパネルの最大比率

// パネル開閉系アニメーション（右パネル / タイムラインパネル / CommentField 移動）は
// 同時に動くため、速さとカーブを 1 か所で共有する。
const PANEL_MOTION_DURATION_SEC = 0.3;
const PANEL_MOTION_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1];
/** framer-motion 用 */
const PANEL_MOTION_TRANSITION = {
  duration: PANEL_MOTION_DURATION_SEC,
  ease: PANEL_MOTION_EASE,
};
/** CSS transition 用（`transition: <prop> ${PANEL_MOTION_CSS}` の形で使う） */
const PANEL_MOTION_CSS =
  `${PANEL_MOTION_DURATION_SEC}s cubic-bezier(${PANEL_MOTION_EASE.join(', ')})`;
/** visibility を transform / opacity の完了まで遅らせるための delay 指定 */
const panelVisibilityCss = (visible: boolean) =>
  `visibility 0s linear ${visible ? '0s' : `${PANEL_MOTION_DURATION_SEC}s`}`;

// ===== 比較グループ化 =====

/** `ベース名_a` 形式（末尾がアンダースコア + アルファベット1文字）の検出 */
const COMPARISON_SUFFIX_RE = /^(.+)_([a-zA-Z])$/;

/**
 * variationGroups から `_a` / `_b` .. サフィックスの比較グループを検出し、
 * スライド列（PreviewSlide[]）を構成する。
 * 同一ベース名のグループが2件以上ある場合のみ比較グループ化し、
 * 1件だけの場合は従来どおり個別スライドとして扱う。
 * スライド順は先頭エントリの variation_order 位置を維持する。
 */
function buildPreviewSlides(groups: VariationGroup[]): PreviewSlide[] {
  const byBase = new Map<string, { group: VariationGroup; letter: string }[]>();
  groups.forEach(group => {
    const m = group.variationName.match(COMPARISON_SUFFIX_RE);
    if (!m) return;
    const arr = byBase.get(m[1]) || [];
    arr.push({ group, letter: m[2] });
    byBase.set(m[1], arr);
  });

  const comparisonBases = new Set(
    Array.from(byBase.entries())
      .filter(([, arr]) => arr.length >= 2)
      .map(([base]) => base),
  );

  const slides: PreviewSlide[] = [];
  const emittedBases = new Set<string>();
  groups.forEach(group => {
    const m = group.variationName.match(COMPARISON_SUFFIX_RE);
    const base = m?.[1];
    if (base && comparisonBases.has(base)) {
      if (emittedBases.has(base)) return;
      emittedBases.add(base);
      const entries: ComparisonEntry[] = (byBase.get(base) || [])
        .slice()
        .sort((a, b) => a.letter.toLowerCase().localeCompare(b.letter.toLowerCase()))
        .map(({ group: g, letter }) => ({ label: letter.toUpperCase(), group: g }));
      slides.push({ kind: 'comparison', baseName: base, entries });
    } else {
      slides.push({ kind: 'single', group });
    }
  });
  return slides;
}

/** スライドの React key / DnD id に使う代表メディア（サムネイル表示にも使用） */
const slideThumbTake = (slide: PreviewSlide): MediaItem => {
  if (slide.kind === 'single') return slide.group.latestTake;
  if (slide.kind === 'comparison') return slide.entries[0].group.latestTake;
  return slide.segment.videoItem;
};

/**
 * スライドの React key / メディア ref 登録キー。
 * MA スライドは同一動画を複数セグメントで参照できるため、
 * メディア ID ではなくセグメント連番でユニーク化する。
 */
const slideKey = (slide: PreviewSlide): string => {
  if (slide.kind === 'single') return slide.group.latestTake.id;
  if (slide.kind === 'comparison') return `cmp-${slide.baseName}`;
  return `ma-${slide.segmentIndex}`;
};

/** スライドの表示名（single はバリエーション名、comparison はベース名） */
const slideName = (slide: PreviewSlide): string => {
  if (slide.kind === 'single') return slide.group.variationName;
  if (slide.kind === 'comparison') return slide.baseName;
  return slide.segment.videoItem.variation_name;
};

/**
 * サイドバーサムネイルのツールチップ。
 * comparison は1アイコンに候補全体をまとめて表示するため、
 * 内包する候補ラベル（A・B ..）を併記して見落としを防ぐ。
 */
const slideTooltip = (slide: PreviewSlide): string => {
  if (slide.kind === 'single') return slide.group.variationName;
  if (slide.kind === 'comparison')
    return `${slide.baseName}（候補 ${slide.entries.map(e => e.label).join('・')}）`;
  return `${slide.segment.videoItem.variation_name}（MA）`;
};

/** サイドバーサムネイルのバッジ用: comparison グループの候補数（single / ma は 0） */
const slideEntryCount = (slide: PreviewSlide): number =>
  slide.kind === 'comparison' ? slide.entries.length : 0;

/** スライドに含まれる全 VariationGroup（ma スライドは並び替え対象外のため空） */
const slideGroups = (slide: PreviewSlide): VariationGroup[] => {
  if (slide.kind === 'single') return [slide.group];
  if (slide.kind === 'comparison') return slide.entries.map(e => e.group);
  return [];
};

// ===== ヘルパー関数 =====
const sortCommentsByDate = (comments: ShotComment[]): ShotComment[] => {
  return [...comments].sort((a, b) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
};

/** ディープリンク到着コメントの一時ハイライト（行背景をふわっと点灯 → 消灯） */
const commentHighlightPulse = keyframes`
  0%, 55% { background-color: rgba(0, 154, 206, 0.14); }
  100% { background-color: transparent; }
`;

/**
 * コメント 1 件のラッパー。`?comment=` ディープリンクで到着したコメントに
 * `deep-link-highlight` を付けて一時ハイライトする（色は --soremo-blue 相当。
 * projectAmbient と同じ理由で color-mix は使わない）。
 */
const CommentMotionItem = styled(motion.div)`
  &.deep-link-highlight {
    animation: ${commentHighlightPulse} 2.4s ease-out both;
    border-radius: 8px;
  }
`;

/**
 * PATCH 単体応答で receivers が省略された場合のみ、既存ローカルの既読を保持する。
 * 空配列 [] は「既読なし」の意図とみなしマージしない。
 */
function mergeShotCommentFromPatch(prev: ShotComment | undefined, incoming: ShotComment): ShotComment {
  if (prev?.receivers?.length && incoming.receivers === undefined) {
    return { ...incoming, receivers: prev.receivers };
  }
  return incoming;
}

/** MediaPreview に渡す共通 props（フルコントロール表示用） */
const MEDIA_PREVIEW_FULL_PROPS = {
  autoPlay: false,
  showMenu: true,
  showDownloadIcon: true,
  showStatusBar: false,
  controls: false,
  hoverPlay: false,
  showWaveform: true,
  disableHoverEffect: true,
  menuOutside: true,
  showProgressBar: true,
  showRemainingTimeOnPlay: true,
  showRestartOnPause: true,
};

/** MediaPreview に渡す共通 props（サムネイル表示用） */
const MEDIA_PREVIEW_THUMB_PROPS = {
  autoPlay: false,
  controls: false,
  showStatusBar: false,
  showWaveform: false,
};

/**
 * サイドバーサムネイル用の MediaPreview props。
 * メディアアイテムには旧環境の content_type が無いため、オーディオは波形 JSON を
 * 渡して生成サムネイルの音の性格推定に使わせる。
 */
const sidebarThumbPreviewProps = (take: MediaItem): Record<string, unknown> =>
  take.file_type === 'audio' && take.has_waveform && take.waveform_url
    ? { ...MEDIA_PREVIEW_THUMB_PROPS, waveformUrl: take.waveform_url }
    : MEDIA_PREVIEW_THUMB_PROPS;

/** デスクトップ: メディアプレビューの sizeMode（幅優先 + maxHeight で高さ制約） */
const DESKTOP_SIZE_MODE_BASE = {
  mode: 'auto' as const,
  objectFit: 'contain' as const,
};

/** デスクトップ: 比較グループ内メディアの sizeMode（複数並べるため高さを抑える） */
const COMPARISON_SIZE_MODE_DESKTOP = {
  mode: 'auto' as const,
  objectFit: 'contain' as const,
  maxHeight: '40dvh',
};

/**
 * SP: メディアはスライド幅いっぱいに広げる（single / take / 比較 / MA 共通）。
 * MediaPreview は sizeMode の参照が変わるたびに派生 props を作り直すため、
 * インライン生成せずこの定数を使い回す。
 */
const MOBILE_SIZE_MODE: SizeMode = {
  mode: 'fixed-width',
  width: '100%',
};

/** コメントリストと CommentField の間に確保する視覚的マージン */
const COMMENT_LIST_BOTTOM_PADDING_BUFFER = 16;
/** ResizeObserver 初回計測前に確保しておく padding-bottom の下限 */
const COMMENT_LIST_BOTTOM_PADDING_MIN = 48;

/** コメント一覧の 1 ページあたり取得件数（無限スクロールの粒度） */
const COMMENT_PAGE_LIMIT = 20;
/** 一覧上端からこの距離まで近づいたら過去コメントを追加読み込みする */
const COMMENT_LOAD_MORE_THRESHOLD_PX = 200;
/** コメント一覧のスクロール判定を間引く間隔 */
const COMMENT_SCROLL_THROTTLE_MS = 50;
/** BE のページネーション上限（ShotCommentPagination.max_limit）。これを超えて要求しても切り詰められる */
const COMMENT_MAX_PAGE_LIMIT = 100;

// =============================================
//  Styled Components — 共通
// =============================================

/**
 * プレビューの全画面ベース。document.body へポータルされるため、
 * ProjectWrapper の --soremo-background（プロジェクト tint）は継承されない。
 * プロジェクト文脈では解決済みの tint を $background で受け取り、
 * 「プロジェクト内のページ」の感覚を維持する（/preview 単独ルートは白のまま）。
 */
const ModalOverlay = styled.div<{ $background?: string }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  background-color: ${p => p.$background || '#fff'};
  overflow: hidden;
`;

const UploadModalTitle = styled.h3.attrs({ className: 'heading-13' })`
  margin: 0;
  text-align: center;
  color: var(--soremo-text);
`;

/** テイクインジケーター（ドット）共通 */
const TakeDotsRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 8px 0;
`;

const TakeDot = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => (p.$active ? 'var(--soremo-blue)' : 'rgba(0,0,0,0.2)')};
  transition: background 0.2s;
  cursor: pointer;
`;

/** デスクトップ: メディア下端にオーバーレイするテイクドット */
const DesktopTakeDotsOverlay = styled(TakeDotsRow)`
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  z-index: 6;
  padding: 0;
`;

/** モバイル専用: コメントオーバーレイを開く FAB（アイコン + 件数ラベル） */
const CommentIconWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  gap: 0;
`;

/** テイクナビゲーションボタン（矢印オーバーレイ） */
const TakeNavBtn = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  ${p => p.$side}: 8px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .material-symbols-rounded {
    font-size: 20px;
  }
`;

// =============================================
//  Styled Components — PC / iPad (Desktop)
// =============================================

const DesktopLayout = styled.div`
  display: flex;
  height: 100dvh;
  width: 100%;
  /* 右パネルの退避（閉時に画面右外へスライド）を横スクロールにしない */
  overflow: hidden;
`;

/** 左サイドバー: 上=戻る / 中央=サムネイル列（スクロール領域）/ 下=ショット編集 の3分割 */
const LeftSidebar = styled.div`
  width: 80px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${SIDEBAR_PADDING_TOP}px 0 ${SIDEBAR_PADDING_BOTTOM}px;
`;

/** 左サイドバー上部: 戻るボタン（サムネと独立してスクロールしない） */
const SidebarHeader = styled.div`
  flex-shrink: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-bottom: 16px;
`;

/** 左サイドバー: バリエーションサムネイルのスクロール領域 */
const SidebarThumbScroller = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 8px;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }
`;

/**
 * サムネイル群のラッパー。justify-content: center はあふれた際に上端が
 * スクロール不能になるため、auto マージンで「収まる時は中央寄せ /
 * あふれた時は上から配置」を実現する。
 */
const SidebarThumbList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SIDEBAR_THUMB_GAP}px;
  margin: auto 0;
`;

/** 左サイドバー: 画面左下に固定するショット編集モーダルの入口（サムネと独立してスクロールしない） */
const SidebarFooter = styled.div`
  flex-shrink: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 8px;
`;

/** 中央カラム: スクロール領域 + フロート情報行 + （タイムラインモード時）下部パネル */
const CenterColumn = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

/**
 * スクロール領域 + フロート情報行のラッパー。
 * SlideInfoRow の absolute 配置基準をこのラッパーにすることで、
 * タイムラインパネル表示時も情報行がメディア領域内にとどまる。
 *
 * $collapsed: オーディオのタイムラインモード用。プレビュー本体が不要なため
 * 情報行が収まる高さまで畳み、残りをタイムラインパネルへ譲る。
 */
const ScrollerWrap = styled.div<{ $collapsed?: boolean }>`
  position: relative;
  flex: ${p => (p.$collapsed ? '0 0 168px' : '1 1 0px')};
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* タイムラインモード（オーディオ）の畳み/展開をなめらかに */
  transition: flex-basis ${PANEL_MOTION_CSS}, flex-grow ${PANEL_MOTION_CSS};
`;

/** スクロール可能な領域（スナップスクロール） */
const CenterColumnScroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scroll-snap-type: y proximity;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }
`;

/** 1 バリエーション = 1 画面スライド */
const VariationSlide = styled.div`
  scroll-snap-align: start;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
  gap: 4px;
`;

/** メディアプレビュー + テイクナビオーバーレイのコンテナ */
const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;


/** デスクトップ: テイク横スクロールカルーセル */
const DesktopTakeCarousel = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  display: flex;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    height: 0;
  }
`;

/** デスクトップ: テイクカルーセルの1スライド */
const DesktopTakeSlide = styled.div`
  scroll-snap-align: start;
  flex: 0 0 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/** テイクバッジの位置基準となる最小ラッパー */
const MediaOverlay = styled.div`
  position: relative;
  width: 100%;
  max-width: ${DESKTOP_MEDIA_MAX_WIDTH};
  max-height: 100%;
`;

/**
 * MA オプション切替チップの列（情報欄の最上段。オプションごとに改行）。
 * 収まらない場合はスクロールする。
 * スクロール領域がチップのホバー影（FilterChip: 2px 4px 8px 3px）を切らないよう、
 * 影の広がり分の padding を取り、同じ量の負の margin で配置を打ち消す
 */
const MA_OPTION_CHIPS_BLEED = { top: 8, right: 14, bottom: 16, left: 10 } as const;
const MA_OPTION_CHIPS_MARGIN_BOTTOM = 8;
const MaOptionChipsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  max-height: 40vh;
  padding: ${MA_OPTION_CHIPS_BLEED.top}px ${MA_OPTION_CHIPS_BLEED.right}px
    ${MA_OPTION_CHIPS_BLEED.bottom}px ${MA_OPTION_CHIPS_BLEED.left}px;
  margin: -${MA_OPTION_CHIPS_BLEED.top}px -${MA_OPTION_CHIPS_BLEED.right}px
    ${MA_OPTION_CHIPS_MARGIN_BOTTOM - MA_OPTION_CHIPS_BLEED.bottom}px -${MA_OPTION_CHIPS_BLEED.left}px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/** 情報 + アクション行（フロート: プレビュー上に重ねて表示） */
const SlideInfoRow = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 8px 0 4px;
  gap: 16px;
  z-index: 10;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

/** 情報テキスト（左側） */
const SlideInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.9), 0 0 16px rgba(255, 255, 255, 0.7);
`;

/** アクションアイコン（右側・縦並び） */
const SlideInfoActions = styled.div<{ $timeline?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;

  /* CircularFab の回転テキストはボタン円の外周に出るため、右端 0 のままだと
     ビューポートからはみ出す。タイムラインモードでは 16px 左に寄せて収め、
     通常時（右にコメントパネルが余白になる）は寄せずに下余白だけ広げる */
  ${p =>
    p.$timeline
      ? `
        padding-right: 16px;
        margin-bottom: 8px;
      `
      : `
        padding-right: 0;
        margin-bottom: 24px;
      `}
`;

/** ドラッグ可能な境界線 */
const ResizeDivider = styled.div<{ $isDragging: boolean }>`
  width: 6px;
  flex-shrink: 0;
  cursor: col-resize;
  background: ${p => (p.$isDragging ? 'var(--soremo-blue)' : 'transparent')};
  transition: background 0.15s;
  position: relative;
  z-index: 5;

  &:hover {
    background: rgba(0, 154, 206, 0.15);
  }
`;

/**
 * 右パネル（コメントエリア）の外枠。レイアウト上の幅だけを受け持ち、
 * 見た目のスライドは内側の PanelInner（固定幅 + translateX）が担う。
 * $instant: メディア FLIP（開閉時にメディアを transform で滑空させる演出）中は
 * 幅を即時切替する。幅の transition が残っていると FLIP と二重に動いて
 * レイアウトが波打つため。
 * $dragging: 境界線ドラッグ中は transition を切ってカーソルへ即追従させる。
 */
const RightPanel = styled.div<{
  $width: number;
  $isOpen: boolean;
  $instant?: boolean;
  $dragging?: boolean;
}>`
  width: ${p => (p.$isOpen ? p.$width : 0)}px;
  flex-shrink: 0;
  height: 100%;
  position: relative;
  transition: ${p =>
    p.$instant || p.$dragging ? 'none' : `width ${PANEL_MOTION_CSS}`};
`;

/**
 * 右パネルの内側。パネル実幅で固定し、外枠の幅とは独立に translateX で
 * 右端からスライドイン/アウトさせる。外枠で切り抜かない（overflow なし）ため、
 * メディア FLIP 中に外枠の幅が即時 0 になっても、内側はスライドで退場できる。
 * 中央カラムのフロート UI（SlideInfoRow: z10 / 再オープンボタン: z11）より
 * 上に重ねてスライドさせる。
 */
const PanelInner = styled.div<{ $width: number; $isOpen: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: ${p => p.$width}px;
  height: 100%;
  display: flex;
  flex-direction: column;
  /* 透過にして ModalOverlay 側のアンビエント背景（プロジェクトのバナー色
     由来の tint + ブロブ）をコメントエリアにもそのまま見せる。
     プロジェクト外では ModalOverlay が #fff のため従来どおり白のまま */
  background: transparent;
  z-index: 12;
  transform: translateX(${p => (p.$isOpen ? '0%' : '100%')});
  visibility: ${p => (p.$isOpen ? 'visible' : 'hidden')};
  transition: transform ${PANEL_MOTION_CSS},
    ${p => panelVisibilityCss(p.$isOpen)};
`;

/**
 * コメントフィルターバーはパネル上端にフロート（TopAppBarFloat）させるため、
 * コメント一覧側は上端にバー高さ分のインセットを確保する。スクロール中の
 * コメントはインセットを通過してパネル上端まで描画される（= バーの下をくぐる）。
 */
const COMMENT_LIST_TOP_INSET = TOP_APP_BAR_HOME_HEIGHT + 8;

/** パネル閉時に中央カラム右上へ浮かせる再オープンボタン（フェードで出入り） */
const PanelOpenFloating = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 11;
  opacity: ${p => (p.$visible ? 1 : 0)};
  visibility: ${p => (p.$visible ? 'visible' : 'hidden')};
  transition: opacity ${PANEL_MOTION_CSS},
    ${p => panelVisibilityCss(p.$visible)};
`;

/**
 * TimelinePanel 開閉のアニメーションラッパー。
 * - fixed（動画 / MA）: height 0 ↔ auto（内容の固有高さ）で下から展開・収納
 * - fill（オーディオ）: flex-grow 0 ↔ 1 で残り高さいっぱいまで展開・収納
 *
 * 伸ばし方が違う = アニメーションするプロパティが違うため、切り替え時は
 * key ごと差し替える（TIMELINE_REVEAL_MOTION のキーをそのまま key に使う）。
 */
const TIMELINE_REVEAL_MOTION = {
  fixed: {
    initial: { height: 0, opacity: 0 },
    animate: { height: 'auto', opacity: 1 },
    exit: { height: 0, opacity: 0 },
  },
  fill: {
    initial: { flexGrow: 0, opacity: 0 },
    animate: { flexGrow: 1, opacity: 1 },
    exit: { flexGrow: 0, opacity: 0 },
  },
} as const;

const TimelineReveal = styled(motion.div)<{ $fill?: boolean }>`
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  ${p =>
    p.$fill &&
    `
    flex-basis: 0;
    min-height: 0;
  `}
`;

const DesktopCommentList = styled.div<{ $bottomPadding: number }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: ${COMMENT_LIST_TOP_INSET}px 16px
    ${({ $bottomPadding }) => $bottomPadding}px 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
`;

// CommentField をコメント一覧にオーバーレイ表示する。CommentField は半透明 + backdrop-filter
// なのでスクロール中は背後のメッセージが透けて見える（DM / TalkRoom と同じ挙動に統一）。
// motion.div なのは layoutId でタイムラインパネル側の入力欄と相互アニメーションさせるため。
const DesktopCommentFieldWrapper = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 4px 16px;
  display: flex;
  justify-content: center;
  z-index: 2;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

// =============================================
//  Styled Components — SP (Mobile)
// =============================================

// width: 100% は DropZoneOverlay（align-items: center）の中で内容幅まで縮まないため必須。
const MobileLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100dvh;
  overflow: hidden;
`;

const MobileHeader = styled.div`
  position: fixed;
  top: 8px;
  left: 8px;
  z-index: 12;
  display: flex;
  align-items: center;
`;

const MobileScroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }
`;

const MobileVariationSlide = styled.div`
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const MobileMediaArea = styled.div`
  position: relative;
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
`;

/** SP: 比較グループスライドの内部（縦に長いため内部スクロール可能にする） */
const MobileComparisonInner = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 8px 0;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }
`;

const MobileTakeCarousel = styled.div`
  width: 100%;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  display: flex;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    height: 0;
  }
`;

const MobileTakeSlide = styled.div`
  scroll-snap-align: start;
  flex: 0 0 100%;
  position: relative;
`;

/** SP フッター: 情報 + アクション行（PC の SlideInfoRow に相当） */
const MobileFooterRow = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 16px 8px;
  gap: 16px;
  background: linear-gradient(transparent, rgba(255,255,255,0.85) 30%);
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const MobileFooterActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  margin-right: 4px;
`;

/** SP コメントオーバーレイ（スライドアップ） */
const MobileCommentOverlay = styled.div<{
  $visible: boolean;
  $background?: string;
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 15;
  /* メディアの上に重なる全画面オーバーレイのため不透明が必要。
     プロジェクト内ではアンビエントの tint 色を敷いて白一色を避ける */
  background: ${p => p.$background || '#fff'};
  display: flex;
  flex-direction: column;
  transform: translateY(${p => (p.$visible ? '0' : '100%')});
  transition: transform 0.3s ease-out;
`;

const MobileCommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

// フロートするフィルターバーの配置基準（ヘッダー下からオーバーレイ下端まで）
const MobileCommentBody = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const MobileCommentList = styled.div<{ $bottomPadding: number }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: ${COMMENT_LIST_TOP_INSET}px 8px
    ${({ $bottomPadding }) => $bottomPadding}px 8px;
`;

// CommentField をコメント一覧にオーバーレイ表示する（デスクトップと同じ方式）。
const MobileCommentFieldWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 4px 16px;
  display: flex;
  justify-content: center;
  z-index: 2;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

// =============================================
//  Component
// =============================================

const Preview: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, shotId } = useParams<{ projectId: string; shotId: string }>();

  // Refs
  const centerColumnRef = useRef<HTMLDivElement>(null);
  const mobileScrollerRef = useRef<HTMLDivElement>(null);
  const variationSlideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mobileCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const desktopTakeCarouselRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const mediaRefs = useRef<Record<string, MediaPreviewHandle | null>>({});
  const mediaContainerRef = useRef<HTMLDivElement>(null);

  // ブラウザ実測 fps（BE の frame_rate 未解析メディアの補完。キー = メディアアイテム ID）
  const [probedFpsById, setProbedFpsById] = useState<Record<string, number>>({});

  // CommentField 高さ追跡。用途:
  //  コメントリストの padding-bottom を実高さに追従させ、テキストエリアが伸びても
  //  最下部コメントが隠れないようにする。
  // renderDesktop / renderMobile は排他描画なので、ref は両方の wrapper に付与してOK。
  const {
    ref: commentFieldWrapperRef,
    element: commentFieldWrapperEl,
    height: commentFieldHeightObserved,
  } = useObservedHeight<HTMLDivElement>();
  // Shift+M ショートカットからコメント入力欄を操作するための handle。
  // CommentField はデスクトップ / タイムライン / SP で排他描画のため単一 ref で
  // 問題ないが、タイムラインパネルは AnimatePresence で退場が遅延し新旧が一瞬
  // 併存するので、コールバック ref で null（旧側のデタッチ）を無視する。
  const commentFieldHandleRef = useRef<CommentFieldHandle | null>(null);
  const attachCommentFieldHandle = useCallback((handle: CommentFieldHandle | null) => {
    if (handle) commentFieldHandleRef.current = handle;
  }, []);

  const shotFromState = (location.state as any)?.shot;
  const openShotEditModalFromState = (location.state as any)?.openShotEditModal;
  // HOME 進行中ホイールからの共有エレメント遷移（クローンがメディア位置へ着地
  // するアニメーション中）。ローディングスピナーがクローン越しに透けないよう
  // 白背景のみ表示する。
  const arrivedViaSharedTransition = !!(location.state as any)?.sharedTransition;

  // ===== API =====
  const { data: shotDetailData } =
    useGetShotDetailQuery({ project_id: projectId || '', shot_id: shotId || '' }, { skip: !projectId || !shotId });

  const { data: mediaItemsData, isLoading: isLoadingMediaItems, error: mediaItemsError, refetch: refetchMediaItems } =
    useGetMediaItemsQuery({ shot_id: shotId || '' }, { skip: !shotId });

  // include_replies: BE は既定でトップレベルのみ返すため、返信コメントを一覧に
  // 載せるには明示指定が必要（返信は parent_id 付きでフラットに返る）。
  const { data: commentsData, isLoading: isLoadingComments, error: commentsError } =
    useGetShotCommentsQuery(
      {
        shot_id: shotId || '',
        filter: 'all',
        include_replies: true,
        limit: COMMENT_PAGE_LIMIT,
        offset: 0,
      },
      { skip: !shotId },
    );

  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(projectId!, { skip: !projectId });

  // プロジェクト文脈のアンビエント背景（全画面モーダルでも「プロジェクト内の
  // ページ」の感覚を保つ）。ProjectWrapper と同一クエリのためキャッシュを共有し、
  // プロジェクト外（/preview 単独ルート）ではアンビエントなしの白背景に戻る。
  const { data: previewProjectData } = useGetProjectQuery(
    { project_id: projectId || '' },
    { skip: !projectId },
  );
  const previewProject = previewProjectData?.data?.project;
  const projectAmbient =
    projectId && previewProject
      ? getProjectAmbient(previewProject.banner_color)
      : null;

  const [fetchMoreComments] = useLazyGetShotCommentsQuery();
  const [fetchCommentDetail] = useLazyGetShotCommentDetailQuery();
  const [sendComment, { isLoading: isSending }] = useSendShotCommentMutation();
  const [updateComment] = useUpdateShotCommentMutation();
  const [deleteComment] = useDeleteShotCommentMutation();
  const [markCommentFilesAsDownloaded] = useMarkCommentFilesAsDownloadedMutation();
  const [markShotCommentsAsRead] = useMarkShotCommentsAsReadMutation();
  const [updateShotStatus] = useUpdateShotStatusMutation();
  const [updateMediaItemSilent] = useUpdateMediaItemSilentMutation();
  const [updateMediaItemTags] = useUpdateMediaItemTagsMutation();

  // ===== State =====
  const [currentVariation, setCurrentVariation] = useState(0);
  const [takeIndices, setTakeIndices] = useState<Record<number, number>>({});
  const [showComments, setShowComments] = useState(true);
  // コメントパネル開閉時のメディア FLIP（Home ⇄ Preview と同じ滑空演出）。
  // panelInstant: FLIP 中はパネル幅の CSS transition を切って即時切替にする。
  // panelFlipRef: トグル直前に測ったメディアの視覚矩形（useLayoutEffect で消費）。
  const [panelInstant, setPanelInstant] = useState(false);
  const panelFlipRef = useRef<{ el: HTMLElement; firstVisualRect: DOMRect } | null>(null);
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    if (typeof window === 'undefined') return 360;
    return Math.round((window.innerWidth - LEFT_SIDEBAR_WIDTH) * COMMENT_PANEL_RATIO);
  });
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const [showMobileComments, setShowMobileComments] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= DESKTOP_BREAKPOINT : true,
  );
  const [filterMode, setFilterMode] = useState<FilterMode>('in_progress');
  const [searchText, setSearchText] = useState('');
  const [allComments, setAllComments] = useState<ShotComment[]>([]);
  const allCommentsRef = useRef(allComments);
  allCommentsRef.current = allComments;

  // コメント一覧の無限スクロール（上方向に過去コメントを追加読み込み）
  const [nextCommentOffset, setNextCommentOffset] = useState(0);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
  const hasInitializedCommentsRef = useRef(false);
  // 追加読み込みを始める直前の scrollHeight。伸びた差分だけ scrollTop を足して
  // 閲覧位置を維持するために使う。0 以外の間は「位置補正待ち」を意味する。
  const commentPrependAnchorRef = useRef(0);
  const isCommentScrollThrottledRef = useRef(false);

  const commentListScrollRef = useRef<HTMLDivElement>(null);
  const pendingReadCommentIds = useRef<Set<string>>(new Set());
  const readDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commentElementRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const [isCommentListScrollComplete, setIsCommentListScrollComplete] = useState(false);
  const isFirstCommentScrollForShotRef = useRef(true);
  // コメントディープリンク適用中は「件数変化で最下部へ」の自動スクロールを抑止
  const deepLinkAutoScrollLockRef = useRef(false);

  const [commentToEdit, setCommentToEdit] = useState<ShotComment | null>(null);
  const [commentToReply, setCommentToReply] = useState<ShotComment | null>(null);
  const [commentToDelete, setCommentToDelete] = useState<ShotComment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFilesWithPath, setSelectedFilesWithPath] = useState<FileWithPath[]>([]);
  const [pinData, setPinData] = useState<{ media_item_id: string } | null>(null);
  const [playingCommentId, setPlayingCommentId] = useState<string | null>(null);
  const [editExistingFiles, setEditExistingFiles] = useState<ExistingAttachedFile[]>([]);
  const [editFilesChanged, setEditFilesChanged] = useState(false);
  const [showShotEditModal, setShowShotEditModal] = useState(false);
  const [showMaExportModal, setShowMaExportModal] = useState(false);

  useEffect(() => {
    if (openShotEditModalFromState) {
      setShowShotEditModal(true);
      // openShotEditModal だけを外し、previewQueue 等のほかの state とクエリは
      // 引き継ぐ（上書きすると ⌥[ / ⌥] のショット移動が効かなくなる）
      const restState = { ...(location.state as Record<string, unknown> | null) };
      delete restState.openShotEditModal;
      navigate(location.pathname + location.search, { replace: true, state: restState });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [playingStates, setPlayingStates] = useState<Record<string, boolean>>({});
  const [hiddenBadges, setHiddenBadges] = useState<Record<string, boolean>>({});
  const hiddenBadgesRef = useRef(hiddenBadges);
  hiddenBadgesRef.current = hiddenBadges;

  useEffect(() => {
    const timers: Record<string, ReturnType<typeof setTimeout>> = {};
    Object.entries(playingStates).forEach(([id, playing]) => {
      if (playing && !hiddenBadgesRef.current[id]) {
        timers[id] = setTimeout(() => {
          setHiddenBadges(prev => ({ ...prev, [id]: true }));
        }, 3000);
      } else if (!playing) {
        setHiddenBadges(prev => ({ ...prev, [id]: false }));
      }
    });
    return () => Object.values(timers).forEach(clearTimeout);
  }, [playingStates]);

  // MediaContainer の高さを測定し、絶対値 maxHeight として sizeMode に反映
  const [mediaMaxHeight, setMediaMaxHeight] = useState<string>('100dvh');

  useEffect(() => {
    const el = mediaContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect.height;
      if (h > 0) setMediaMaxHeight(`${Math.floor(h)}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // コメントパネルの表示状態（デスクトップは showComments、モバイルは showMobileComments）。
  // 非表示時は高さを 0 扱いにして一覧 padding を安全側に戻す。
  const commentsVisible = isDesktop ? showComments : showMobileComments;
  const commentFieldHeight = commentsVisible && commentFieldWrapperEl
    ? commentFieldHeightObserved
    : 0;

  const desktopSizeMode = useMemo(() => ({
    ...DESKTOP_SIZE_MODE_BASE,
    maxHeight: mediaMaxHeight,
  }), [mediaMaxHeight]);

  // 自動再生用: 前回のメディアアイテムID
  const prevMediaItemIdRef = useRef<string | null>(null);

  // ユーザーが一度でもメディアを再生操作したかどうか（初回表示時の自動再生を抑制）
  const hasUserInitiatedPlaybackRef = useRef(false);

  // Approval
  const { isApproved, canApprove, isLoading: isApprovalLoading, toggleApproval, userPosition, isReadOnly } = useShotApproval({
    projectId: projectId || '',
    shotId: shotId || '',
    currentStatus: shotFromState?.status ?? 0,
  });

  // クライアント側（CLIENT / REVIEWER）か。FAB の variant・チクチクフィルター
  //（AI 校正）・ショット編集権限などの判定に使う
  const isClientUser = userPosition === 'CLIENT' || userPosition === 'REVIEWER';

  // プロデューサー側（PRODUCER / DIRECTOR / ADMIN）か。返信君（AI 返信生成）・
  // ショット編集権限などの判定に使う
  const isProducerUser = userPosition === 'PRODUCER' || userPosition === 'DIRECTOR' || userPosition === 'ADMIN';

  // CircularFab の variant を決定
  const fabVariant = isClientUser ? 'client' as const : 'producer' as const;

  const [fadingOut, setFadingOut] = useState(false);
  const approvalTransitionRef = useRef(false);

  // ショット編集（メディアアイテム追加・並び替え・MA タイムライン編集）が可能か。
  // プロデューサー側に加え、クライアント側（CLIENT/REVIEWER）にも開放する。
  // ただし閲覧のみメンバー（is_read_only）のクライアントは除外（canApprove と同じ扱い）
  const canEditShot = isProducerUser || (isClientUser && !isReadOnly);

  // S3 Upload
  const {
    uploadFiles,
    uploadingFiles,
    isUploading,
    abortFile,
    clearCompleted,
    retryFile,
    retryFiles,
    postProcessLabel,
    withPostProcess,
  } = useS3Upload({
    purpose: 'shot_comment',
    projectId,
    shotId,
    onValidationError: (errors: ValidationError[]) => {
      errors.forEach(e => console.warn(`[Upload] バリデーションエラー: ${e.file.name} - ${e.message}`));
    },
    onFileComplete: (file: UploadingFile) => console.log(`[Upload] 完了: ${file.file.name}`),
    onAllComplete: (files: UploadingFile[]) => console.log(`[Upload] 全完了: ${files.length}件`),
    onError: (file: UploadingFile, error: Error) => console.error(`[Upload] エラー: ${file.file.name}`, error),
  });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });

  // ===== Derived =====
  const currentUserId = useMemo(() => currentUserData?.data?.id, [currentUserData]);

  const currentUserForReceivers = useMemo((): ShotCommentUser | null => {
    if (!currentUserData?.data) return null;
    const user = currentUserData.data;
    return {
      id: user.id,
      display_name: user.stage_name || user.fullname || 'Unknown',
      avatar: user.icon || null,
      position: membershipData?.data?.position as ShotCommentUser['position'],
    };
  }, [currentUserData, membershipData]);

  // 左右判定は共通ユーティリティへ委譲。自分の投稿（author.id === 自分）は
  // membership の position が未取得・不整合でも常に右に出す（左右反転バグの
  // 恒久対策）。
  const isMessageOnRight = useCallback(
    (comment: ShotComment): boolean =>
      isMessageOnRightSide({
        authorId: comment.author?.id,
        authorPosition: comment.author?.position,
        currentUserId,
        viewerPosition: membershipData?.data?.position,
      }),
    [membershipData, currentUserId],
  );

  // 原因調査用の計測: 自分の position が別グループへ変わったら警告を出す。
  // 通常運用では起きないため、発生時は membership 応答の混線
  // （キャッシュ・API 側の不整合）を疑うシグナルになる。
  const prevPositionGroupRef = useRef<ReturnType<typeof positionGroupOf> | null>(null);
  useEffect(() => {
    const position = membershipData?.data?.position;
    if (!position) return;
    const group = positionGroupOf(position);
    const prev = prevPositionGroupRef.current;
    if (prev !== null && prev !== group) {
      console.warn(
        '[Preview] 自分の membership position が別グループへ変化しました。' +
          'コメントの左右反転の原因調査用ログです。',
        { prevGroup: prev, nextGroup: group, position, projectId },
      );
    }
    prevPositionGroupRef.current = group;
  }, [membershipData, projectId]);

  // ===== バリエーショングループ化 =====
  const variationGroups: VariationGroup[] = useMemo(() => {
    const items = mediaItemsData?.data?.media_items || [];
    const groupMap = new Map<string, MediaItem[]>();

    // macOS 由来の NFD 名と NFC 名の同名テイクを同じバリエーションにまとめる
    items.forEach(item => {
      const key = normalizeNfc(item.variation_name);
      const arr = groupMap.get(key) || [];
      arr.push(item);
      groupMap.set(key, arr);
    });

    return Array.from(groupMap.entries())
      .map(([name, takes]) => {
        // テイクを updated_at 降順ソート（最新が先頭）
        const sorted = [...takes].sort(
          (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
        return {
          variationName: name,
          takes: sorted,
          latestTake: sorted[0],
          variationOrder: sorted[0].variation_order,
        };
      })
      .sort((a, b) => a.variationOrder - b.variationOrder);
  }, [mediaItemsData]);

  // ===== MA シーケンス（CSV / RPP）=====
  // ショット内の最新シーケンスファイルを検出してパース・リンクする。
  // シーケンスが有効な場合、リンク済みメディアのスライドは隠れ、
  // 動画行ごとの MA スライド（ミックス再生）が先頭に並ぶ。
  const maState = useMaSequence(mediaItemsData?.data?.media_items);
  const maSequence = maState.sequence;
  // タイムチャート CSV のファイル名ベース（一括ダウンロード同梱 / 書き出しモーダル共通）
  const maTimeChartBaseName = maState.maFileItem
    ? stripExtension(maState.maFileItem.file_name)
    : 'MA';

  // ===== MA オプション候補のプレビュー切替（通常 PREVIEW 画面のチップ） =====
  // タイムライン編集を経由しない確認用の選択状態（保存されず、
  // シーケンス変更 = 新テイク読込・ショット移動でリセットされる）
  const [maPreviewOptionSelections, setMaPreviewOptionSelections] = useState<
    Record<string, string>
  >({});
  // 切替の操作順（末尾 = 最後に切り替えたスロット）。同一セグメントに動画付き
  // スロットが複数ある場合の「後勝ち」の動画解決に使う
  const [maPreviewOptionOrder, setMaPreviewOptionOrder] = useState<string[]>([]);
  useEffect(() => {
    setMaPreviewOptionSelections({});
    setMaPreviewOptionOrder([]);
  }, [maSequence]);

  const handleMaPreviewSelectOption = useCallback((slotKey: string, choiceKey: string) => {
    setMaPreviewOptionSelections(prev => ({ ...prev, [slotKey]: choiceKey }));
    setMaPreviewOptionOrder(prev => [...prev.filter(key => key !== slotKey), slotKey]);
  }, []);

  // プレビュー選択を適用した表示・再生用セグメント（segmentIndex → 適用済みセグメント）。
  // 既定候補のままのセグメントは含めない（元の参照を使い、不要な再スケジュールを防ぐ）
  const maPreviewSegments = useMemo(() => {
    if (!maSequence || maSequence.optionSlots.length === 0) return null;
    const map = new Map<number, MaSegment>();
    maSequence.segments.forEach((segment, segmentIndex) => {
      const slots = maSequence.optionSlots.filter(s => s.segmentIndex === segmentIndex);
      if (slots.length === 0) return;
      const applied = applyOptionSelectionsToSegment(
        segment,
        slots,
        maPreviewOptionSelections,
        maPreviewOptionOrder,
      );
      if (applied !== segment) map.set(segmentIndex, applied);
    });
    return map.size > 0 ? map : null;
  }, [maSequence, maPreviewOptionSelections, maPreviewOptionOrder]);

  // ===== 比較グループ化（_a/_b サフィックス） + MA スライド構成 =====
  const previewSlides: PreviewSlide[] = useMemo(() => {
    const baseSlides = buildPreviewSlides(variationGroups);
    const maFileIds = maState.maFileItemIds;

    // シーケンスファイル自体はサイドバー / スライドに出さない
    let slides =
      maFileIds.size > 0
        ? baseSlides.filter(slide =>
            slideGroups(slide).every(g => g.takes.every(t => !maFileIds.has(t.id))),
          )
        : baseSlides;

    if (!maSequence || maSequence.segments.length === 0) return slides;

    // シーケンスにリンクされたメディアのスライドを隠す（MA スライド経由で再生する）
    const linkedIds = maSequence.linkedItemIds;
    if (linkedIds.size > 0) {
      slides = slides.filter(slide =>
        slideGroups(slide).every(g => g.takes.every(t => !linkedIds.has(t.id))),
      );
    }

    const maSlides: PreviewSlide[] = maSequence.segments.map((segment, segmentIndex) => ({
      kind: 'ma',
      segment,
      segmentIndex,
    }));
    return [...maSlides, ...slides];
  }, [variationGroups, maState.maFileItemIds, maSequence]);

  // 比較グループの選択状態（baseName → 選択中メディア ID）
  const [comparisonSelections, setComparisonSelections] = useState<Record<string, string | null>>({});

  /** 比較グループ内エントリの選択トグル（同じものを再クリックで解除） */
  const handleComparisonSelect = useCallback((baseName: string, mediaItemId: string) => {
    setComparisonSelections(prev => ({
      ...prev,
      [baseName]: prev[baseName] === mediaItemId ? null : mediaItemId,
    }));
  }, []);

  // クライアント側が最後に選択・送信した候補（baseName → 最新テイクID）。
  // ピン付きコメント履歴から導出するため、リロード・他ユーザー画面でも青キープされる。
  const clientKeptSelections = useMemo(() => {
    // 各テイクID → 所属エントリの最新テイクID / ベース名
    const takeToEntry = new Map<string, { baseName: string; latestTakeId: string }>();
    previewSlides.forEach(slide => {
      if (slide.kind !== 'comparison') return;
      slide.entries.forEach(entry => {
        entry.group.takes.forEach(take => {
          takeToEntry.set(take.id, {
            baseName: slide.baseName,
            latestTakeId: entry.group.latestTake.id,
          });
        });
      });
    });

    const map: Record<string, string> = {};
    // allComments は created_at 昇順 → 順に上書きすると最後（最新）が残る
    allComments.forEach(c => {
      if (!c.media_item_id) return;
      const pos = c.author?.position;
      if (pos !== 'CLIENT' && pos !== 'REVIEWER') return;
      const entry = takeToEntry.get(c.media_item_id);
      if (entry) map[entry.baseName] = entry.latestTakeId;
    });
    return map;
  }, [previewSlides, allComments]);

  // ===== 左サイドバー: サムネイルサイズ =====
  // ビューポートに収まらない場合のみ、下限 SIDEBAR_THUMB_MIN まで均等縮小する。
  // それでも収まらない場合は overflow-y: auto のスクロールに委ねる。
  const thumbScrollerRef = useRef<HTMLDivElement>(null);
  const [sidebarThumbSize, setSidebarThumbSize] = useState(SIDEBAR_THUMB_DEFAULT);

  useEffect(() => {
    const el = thumbScrollerRef.current;
    if (!el) return;

    const count = previewSlides.length;
    const recalc = () => {
      if (count === 0) return;
      // ref はサムネイルのスクロール領域を指すため、上部余白・下部の
      // ショット編集アイコン分は含まれず clientHeight をそのまま使える
      const usableHeight = el.clientHeight;
      const gaps = (count - 1) * SIDEBAR_THUMB_GAP;
      const fitSize = Math.floor((usableHeight - gaps) / count);
      setSidebarThumbSize(
        Math.max(SIDEBAR_THUMB_MIN, Math.min(SIDEBAR_THUMB_DEFAULT, fitSize)),
      );
    };

    recalc();
    const observer = new ResizeObserver(recalc);
    observer.observe(el);
    return () => observer.disconnect();
  }, [previewSlides.length, isDesktop]);

  // ===== 左サイドバー DnD =====
  const sidebarSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // MA シーケンスファイルがあるショットでは、スライド順がシーケンス由来（MA スライド）と
  // variation_order 由来の混在になり並び替えの意味が破綻するため DnD を無効化する
  //（並び替えはショット編集モーダルから可能）
  const sidebarDndEnabled = canEditShot && !maState.maFileItem;

  const sidebarSortableIds = useMemo(
    () => previewSlides.map(s => slideThumbTake(s).id),
    [previewSlides],
  );

  const handleSidebarDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !shotId) return;

      const oldIndex = previewSlides.findIndex(s => slideThumbTake(s).id === active.id);
      const newIndex = previewSlides.findIndex(s => slideThumbTake(s).id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(previewSlides, oldIndex, newIndex);

      // スライド順にグループを平坦化して連番を振り直す（比較グループは一塊のまま移動）
      const updates: Array<{ itemId: string; newOrder: number }> = [];
      let orderCounter = 0;
      reordered.forEach(slide => {
        slideGroups(slide).forEach(group => {
          orderCounter += 1;
          if (group.variationOrder !== orderCounter) {
            const newOrder = orderCounter;
            group.takes.forEach(take => {
              updates.push({ itemId: take.id, newOrder });
            });
          }
        });
      });

      if (updates.length === 0) return;

      // 楽観的更新: RTK Query のキャッシュを即座に書き換えてスナップバックを防止
      (dispatch as any)(
        mediaItemsApi.util.updateQueryData('getMediaItems', { shot_id: shotId }, (draft) => {
          updates.forEach(({ itemId, newOrder }) => {
            const item = draft.data.media_items.find(i => i.id === itemId);
            if (item) item.variation_order = newOrder;
          });
        }),
      );

      // バックグラウンドで PATCH を送信（キャッシュ無効化なし — 楽観的更新済み）
      try {
        await Promise.all(
          updates.map(({ itemId, newOrder }) =>
            updateMediaItemSilent({
              shot_id: shotId,
              media_item_id: itemId,
              variation_order: newOrder,
            }).unwrap(),
          ),
        );
      } catch (err) {
        console.error('並び替えエラー:', err);
        refetchMediaItems();
      }
    },
    [previewSlides, shotId, dispatch, updateMediaItemSilent, refetchMediaItems],
  );

  // 現在のスライド / メディアアイテム
  const currentSlide = previewSlides[currentVariation];
  const currentTakeIdx = takeIndices[currentVariation] || 0;
  const currentSlideName = currentSlide ? slideName(currentSlide) : undefined;

  const currentMediaItem: MediaItem | undefined = useMemo(() => {
    if (!currentSlide) return undefined;
    if (currentSlide.kind === 'ma') {
      // MA スライド: コメント・情報表示はセグメントの動画アイテムに紐づける
      return currentSlide.segment.videoItem;
    }
    if (currentSlide.kind === 'single') {
      return currentSlide.group.takes[currentTakeIdx] || currentSlide.group.latestTake;
    }
    // 比較グループ: 選択中エントリがあればそれ、なければ先頭エントリ
    const selectedId = comparisonSelections[currentSlide.baseName];
    const selected = selectedId
      ? currentSlide.entries.find(e => e.group.latestTake.id === selectedId)
      : undefined;
    return (selected ?? currentSlide.entries[0])?.group.latestTake;
  }, [currentSlide, currentTakeIdx, comparisonSelections]);

  // ===== タイムラインモード =====
  // デスクトップで audio/video メディア表示中にコメントパネルを閉じると、
  // 中央カラム下部にタイムラインパネルを表示する（フレーム単位チェックモード）。
  const timelineMode =
    isDesktop &&
    !showComments &&
    (currentSlide?.kind === 'single' || currentSlide?.kind === 'ma') &&
    !!currentMediaItem &&
    isAudioOrVideoType(currentMediaItem.file_type);
  const currentMediaFps = getMediaFps(
    currentMediaItem,
    currentMediaItem ? probedFpsById[currentMediaItem.id] : null,
  );

  // ===== MA タイムライン編集モード =====
  // CSV 由来のシーケンスをタイムライン上で編集し、「更新」で新テイクとして保存する

  // CSV 新テイク保存用のアップローダ（メディア D&D 用とは別インスタンス）
  const { uploadFiles: uploadMaCsvFiles } = useS3Upload({
    purpose: 'media_item',
    projectId,
    shotId,
    skipRegister: true,
  });
  const [createMediaItem] = useCreateMediaItemMutation();

  // バリアント切替・ドロワー対象（シーケンスファイル自体は除外）
  const maLinkableItems = useMemo(() => {
    const items = mediaItemsData?.data?.media_items || [];
    return items.filter(item => !maState.maFileItemIds.has(item.id));
  }, [mediaItemsData, maState.maFileItemIds]);

  // 編集した CSV を同じバリエーション名の新テイクとしてアップロードする
  const handleMaCsvSave = useCallback(
    async (csvText: string, fileName: string) => {
      const maFileItem = maState.maFileItem;
      if (!maFileItem || !shotId) throw new Error('保存対象の MA シーケンスがありません');
      try {
        const file = new File([csvText], fileName, { type: 'text/csv' });
        const [uploaded] = await uploadMaCsvFiles([file]);
        if (!uploaded || uploaded.status !== 'completed' || !uploaded.fileKey) {
          throw new Error(uploaded?.error || 'アップロードに失敗しました');
        }
        await createMediaItem({
          shot_id: shotId,
          file_key: uploaded.fileKey,
          // 既存シーケンスファイルと同じバリエーション名 = 新テイクとして積まれる
          variation_name: maFileItem.variation_name,
          silent: true,
        }).unwrap();
        await refetchMediaItems();
        toast.success('MAシーケンスを新しいテイクとして保存しました');
      } catch (err) {
        console.error('[MA] CSV 保存エラー:', err);
        toast.error('MAシーケンスの保存に失敗しました');
        throw err;
      }
    },
    [maState.maFileItem, shotId, uploadMaCsvFiles, createMediaItem, refetchMediaItems],
  );

  const maTimelineEdit = useMaTimelineEdit({
    sequence: maSequence,
    segmentIndex: currentSlide?.kind === 'ma' ? currentSlide.segmentIndex : null,
    linkableItems: maLinkableItems,
    // 編集保存は常に CSV を生成するため、アクティブが RPP でも保存名は {ステム}.csv にする
    // （variation_name は既存を引き継ぐので RPP と同じテイク履歴に積まれる）
    csvFileName: maState.maFileItem
      ? `${stripExtension(maState.maFileItem.file_name)}.csv`
      : null,
    onSave: handleMaCsvSave,
  });

  // スライド移動・タイムライン非表示で編集モードを終了する
  // （編集対象セグメントと表示中スライドの不一致を防ぐ）
  const { isEditing: maIsEditing, editingSegmentIndex: maEditingIndex, cancelEdit: maCancelEdit } =
    maTimelineEdit;
  useEffect(() => {
    if (!maIsEditing) return;
    const onEditedSlide =
      timelineMode && currentSlide?.kind === 'ma' && currentSlide.segmentIndex === maEditingIndex;
    if (!onEditedSlide) maCancelEdit();
  }, [maIsEditing, maEditingIndex, timelineMode, currentSlide, maCancelEdit]);

  // MA スライドのマルチトラックタイムライン（動画内オーディオ + CSV クリップ）
  // 編集中は編集ワーキングコピー（バリアント切替・レイアウト適用済み）、
  // 非編集中はプレビューのオプション切替を適用したセグメントを表示する
  const currentMaSegment =
    currentSlide?.kind === 'ma'
      ? maTimelineEdit.editedSegment &&
        maTimelineEdit.editingSegmentIndex === currentSlide.segmentIndex
        ? maTimelineEdit.editedSegment
        : maPreviewSegments?.get(currentSlide.segmentIndex) ?? currentSlide.segment
      : null;
  // `_a`/`_b` オプションを持つアイテム ID（波形カラーの出し分け用: あり=blue / なし=light-gray）
  const maVariantItemIds = useMemo(() => {
    const ids = new Set<string>();
    maTimelineEdit.variantGroups.forEach(group => {
      if (group.options.length < 2) return;
      group.memberIds.forEach(id => ids.add(id));
    });
    return ids;
  }, [maTimelineEdit.variantGroups]);
  const maTimelineTracks = useMaTimelineTracks(
    currentMaSegment,
    timelineMode,
    maVariantItemIds,
    maSequence?.videoTrackName,
  );

  // オーディオのタイムラインモード: プレビュー本体が不要なため、
  // メディア領域を情報行だけの高さに畳み、タイムラインパネルを縦に拡張する
  const timelineAudioFull =
    timelineMode && !currentMaSegment && currentMediaItem?.file_type === 'audio';
  /** タイムラインパネルの伸ばし方（TIMELINE_REVEAL_MOTION のキー） */
  const timelineRevealKind = timelineAudioFull ? 'fill' : 'fixed';

  // プレビュー領域の畳み/展開でスライド高さが変わるため、
  // 切替時は現在のバリエーション位置へ即時再スクロールして表示ズレを防ぐ
  const prevTimelineAudioFullRef = useRef(timelineAudioFull);
  useEffect(() => {
    if (prevTimelineAudioFullRef.current === timelineAudioFull) return;
    prevTimelineAudioFullRef.current = timelineAudioFull;
    variationSlideRefs.current[currentVariation]?.scrollIntoView({ block: 'start' });
  }, [timelineAudioFull, currentVariation]);

  // タイムライン用波形（事前計算 JSON → なければ元メディアをブラウザデコード）
  // MA スライドは useMaTimelineTracks 側で取得するため無効化
  const timelineWaveform = useWaveformPeaks({
    mediaItemId: currentMediaItem?.id,
    waveformUrl:
      currentMediaItem?.has_waveform && currentMediaItem.waveform_url
        ? currentMediaItem.waveform_url
        : null,
    // HLS はデコード不可のため original_url（元アップロードファイル）を使う
    fallbackSrc: currentMediaItem?.original_url ?? null,
    enabled: timelineMode && !currentMaSegment,
  });

  // タイムラインのコメントピン（現在メディアにピンされ、タイムコードを持つもの）
  const timelinePins: TimelinePin[] = useMemo(() => {
    if (!timelineMode || !currentMediaItem) return [];
    const pins: TimelinePin[] = [];
    allComments.forEach(c => {
      if (c.media_item_id !== currentMediaItem.id) return;
      const timecode = hasTimecode(c.comment)
        ? extractFirstTimecode(c.comment)
        : c.timecode_start || null;
      if (!timecode) return;
      const seconds = parseTimecode(timecode, currentMediaFps);
      if (seconds == null) return;
      // ポップオーバー本文はタイムコードタグ（[M:SS(.FF)] / [N]）を除いた素のテキストにする
      const text = c.comment.replace(new RegExp(BRACKET_TAG_RE.source, 'g'), '').trim();
      pins.push({
        commentId: c.id,
        authorName: c.author?.display_name || '',
        avatarUrl: c.author?.avatar ?? null,
        timeSeconds: seconds,
        timecodeLabel: timecode,
        text: text || c.comment,
        isResolved: !!c.is_resolved,
      });
    });
    return pins;
  }, [timelineMode, currentMediaItem, allComments, currentMediaFps]);

  // タイムラインモード中は動画内蔵の進捗 UI を無効化（タイムラインと二重になるため）。
  // オーディオはプレビューを畳んで画面外になるため、ビューポート外自動停止も無効化する
  const desktopSlideMediaProps = useMemo(
    () =>
      timelineMode
        ? {
            ...MEDIA_PREVIEW_FULL_PROPS,
            showProgressBar: false,
            showRemainingTimeOnPlay: false,
            showRestartOnPause: false,
            pauseWhenOffscreen: false,
          }
        : MEDIA_PREVIEW_FULL_PROPS,
    [timelineMode],
  );

  // MA スライドの 3 点リーダーに「書き出し」（テロップ付き動画 / ミックス / WAV / CSV）を追加
  const maExtraMenuItems = useMemo<MenuItem[]>(
    () =>
      maSequence ? [{ label: '書き出し', onClick: () => setShowMaExportModal(true) }] : [],
    [maSequence],
  );
  const maDesktopSlideMediaProps = useMemo(
    () =>
      maExtraMenuItems.length > 0
        ? { ...desktopSlideMediaProps, extraMenuItems: maExtraMenuItems }
        : desktopSlideMediaProps,
    [desktopSlideMediaProps, maExtraMenuItems],
  );
  const maMobileSlideMediaProps = useMemo(
    () =>
      maExtraMenuItems.length > 0
        ? { ...MEDIA_PREVIEW_FULL_PROPS, extraMenuItems: maExtraMenuItems }
        : MEDIA_PREVIEW_FULL_PROPS,
    [maExtraMenuItems],
  );

  // MA スライドのオプション切替チップ列（情報欄の最上段）。
  // 切替可能なスロット（候補 2 つ以上）のないセグメント・タイムライン編集中（ドロワー側で切替）は非表示。
  // 再生中は選択中の候補だけを表示し、停止中のみ全候補を表示する
  const renderMaOptionChips = useCallback(
    (segmentIndex: number, playing: boolean) => {
      if (!maSequence || maTimelineEdit.isEditing) return null;
      const slots = maSequence.optionSlots.filter(
        s => s.segmentIndex === segmentIndex && isSelectableOptionSlot(s),
      );
      if (slots.length === 0) return null;
      return (
        <MaOptionChipsColumn>
          {slots.map(slot => (
            <MaOptionSlotChip
              key={slot.key}
              variant="overlay"
              activeOnly={playing}
              slot={slot}
              selectedKey={maPreviewOptionSelections[slot.key] ?? slot.defaultChoiceKey}
              onSelectOption={handleMaPreviewSelectOption}
            />
          ))}
        </MaOptionChipsColumn>
      );
    },
    [
      maSequence,
      maTimelineEdit.isEditing,
      maPreviewOptionSelections,
      handleMaPreviewSelectOption,
    ],
  );

  const shotName =
    shotDetailData?.data?.shot?.shot_name ||
    shotFromState?.shot_name ||
    shotId ||
    'ショット名';

  // ===== 進行中/完了の有無判定 → セグメントオプション生成 =====
  const hasInProgressComments = useMemo(
    () => allComments.some((c) => !c.is_resolved),
    [allComments],
  );
  const hasCompletedComments = useMemo(
    () => allComments.some((c) => c.is_resolved === true),
    [allComments],
  );
  const commentFilterOptions = useMemo(() => {
    const options: { label: string; value: FilterMode }[] = [];
    if (hasInProgressComments) {
      options.push({ label: "進行中", value: "in_progress" });
    }
    if (hasCompletedComments) {
      options.push({ label: "完了", value: "completed" });
    }
    return options;
  }, [hasInProgressComments, hasCompletedComments]);
  const firstAvailableMode = commentFilterOptions[0]?.value ?? "in_progress";
  const availableValues = useMemo(
    () => commentFilterOptions.map((o) => o.value),
    [commentFilterOptions],
  );

  // ===== フィルタリング =====
  const filteredComments = useMemo(() => {
    switch (filterMode) {
      case 'in_progress':
        return allComments.filter(c => !c.is_resolved);
      case 'completed':
        return allComments.filter(c => c.is_resolved === true);
      case 'search': {
        if (!searchText.trim()) return allComments;
        const lower = searchText.toLowerCase();
        return allComments.filter(c => c.comment.toLowerCase().includes(lower));
      }
      default:
        return allComments;
    }
  }, [allComments, filterMode, searchText]);

  const commentCount = filteredComments.length;

  const receiverIncludesCurrentUser = useCallback(
    (receivers: ShotComment['receivers'], uid: number | undefined) => {
      if (uid === undefined) return false;
      return receivers.some(r => String(r.id) === String(uid));
    },
    [],
  );

  const isShotCommentUnread = useCallback(
    (comment: ShotComment): boolean => {
      const uid = currentUserData?.data?.id;
      if (uid === undefined) return false;
      if (String(comment.author?.id) === String(uid)) return false;
      return !receiverIncludesCurrentUser(comment.receivers || [], uid);
    },
    [currentUserData?.data?.id, receiverIncludesCurrentUser],
  );

  const processReadShotComments = useCallback(async () => {
    if (!shotId || pendingReadCommentIds.current.size === 0 || !currentUserForReceivers) return;

    const idsToRead = Array.from(pendingReadCommentIds.current);
    const idsToReadSet = new Set(idsToRead);
    pendingReadCommentIds.current.clear();

    try {
      await markShotCommentsAsRead({
        shot_id: shotId,
        comment_ids: idsToRead,
      }).unwrap();

      const seenAt = new Date().toISOString();
      const self = currentUserForReceivers;
      setAllComments(prev =>
        prev.map(c => {
          if (!idsToReadSet.has(c.id) || receiverIncludesCurrentUser(c.receivers || [], self.id)) {
            return c;
          }
          return {
            ...c,
            receivers: [...(c.receivers || []), { ...self, seen_at: seenAt }],
          };
        }),
      );
    } catch (error) {
      console.warn('[processReadShotComments] 既読登録エラー、リトライ予定:', error);
      idsToRead.forEach(id => pendingReadCommentIds.current.add(id));
      if (readDebounceTimerRef.current) {
        clearTimeout(readDebounceTimerRef.current);
      }
      readDebounceTimerRef.current = setTimeout(() => {
        processReadShotComments();
      }, 3000);
    }
  }, [shotId, currentUserForReceivers, markShotCommentsAsRead, receiverIncludesCurrentUser]);

  const addShotCommentToReadQueue = useCallback(
    (commentId: string) => {
      pendingReadCommentIds.current.add(commentId);
      if (readDebounceTimerRef.current) {
        clearTimeout(readDebounceTimerRef.current);
      }
      readDebounceTimerRef.current = setTimeout(() => {
        processReadShotComments();
      }, 500);
    },
    [processReadShotComments],
  );

  const registerCommentElementRef = useCallback((commentId: string, element: HTMLDivElement | null) => {
    if (element) {
      commentElementRefs.current.set(commentId, element);
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.observe(element);
      }
    } else {
      const existing = commentElementRefs.current.get(commentId);
      if (existing && intersectionObserverRef.current) {
        intersectionObserverRef.current.unobserve(existing);
      }
      commentElementRefs.current.delete(commentId);
    }
  }, []);

  const canObserveShotCommentReads = isDesktop ? showComments : showMobileComments;

  // ===== Handlers (Effects より前に定義: useEffect 内から参照するため) =====

  const handleClose = useCallback(() => {
    // 共有エレメント遷移で来た場合は、表示中メディアをホイールカードへ縮小して
    // 戻す逆アニメーション。検収完了時（fadingOut）はオーバーレイ全体の
    // フェードに任せる（カード自体も消えるため）。
    if (arrivedViaSharedTransition && shotId && !fadingOut) {
      startSharedMediaReturnTransition({
        shotId,
        imageSrc:
          shotDetailData?.data?.shot?.thumbnail ??
          shotFromState?.thumbnail ??
          undefined,
      });
    }
    // アプリ内に戻り先が無い場合（URL 直接アクセス、またはサインイン経由の
    // replace 遷移で到達した場合）はプロジェクトホームへ戻す。
    // React Router が history.state に保持する履歴インデックス idx で判定する
    // （location.key === "default" 判定は replace 遷移で key が変わるため不十分）。
    const historyIdx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (historyIdx <= 0) {
      navigate(projectId ? `/${projectId}` : "/projects", { replace: true });
    } else {
      navigate(-1);
    }
  }, [
    navigate,
    projectId,
    arrivedViaSharedTransition,
    shotId,
    fadingOut,
    shotDetailData,
    shotFromState,
  ]);

  const handleApprovalWithTransition = useCallback(async () => {
    if (approvalTransitionRef.current) return;
    if (isApproved) {
      toggleApproval();
      return;
    }
    window.dispatchEvent(new CustomEvent("approval-start"));
    approvalTransitionRef.current = true;
    try {
      await toggleApproval();
      dispatch(projectsApi.util.invalidateTags(["Projects"]));
      await new Promise<void>(r => setTimeout(r, MICRO_INTERACTION_DURATION));
      setFadingOut(true);
      await new Promise<void>(r => setTimeout(r, FADE_OUT_DURATION));
      handleClose();
    } catch {
      approvalTransitionRef.current = false;
    }
  }, [isApproved, toggleApproval, handleClose, dispatch]);

  // ===== Effects =====

  // プレビュー表示時はグローバルオーディオを ON にする
  // （フローティングのオーディオトグルは廃止。再生時から音が出る状態にする）
  useEffect(() => {
    dispatch(setAudioEnabled(true));
  }, [dispatch]);

  // レスポンシブ
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // コメント初期読み込み（キャッシュ再取得による再実行で追加読み込み分を失わないよう
  // ショットごとに一度だけ適用する）
  useEffect(() => {
    const page = commentsData?.data;
    if (!page?.comments || hasInitializedCommentsRef.current) return;
    hasInitializedCommentsRef.current = true;
    setAllComments(sortCommentsByDate(page.comments));
    setHasMoreComments(page.has_next);
    setNextCommentOffset(page.next_offset || 0);
  }, [commentsData]);

  // ショット切替時: 既読キュー・IO・ページネーションをリセット
  useEffect(() => {
    pendingReadCommentIds.current.clear();
    commentElementRefs.current.clear();
    isFirstCommentScrollForShotRef.current = true;
    hasInitializedCommentsRef.current = false;
    commentPrependAnchorRef.current = 0;
    setNextCommentOffset(0);
    setHasMoreComments(false);
    setIsCommentListScrollComplete(false);
    if (readDebounceTimerRef.current) {
      clearTimeout(readDebounceTimerRef.current);
      readDebounceTimerRef.current = null;
    }
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
      intersectionObserverRef.current = null;
    }
  }, [shotId]);

  // コメント一覧の初回最下部スクロール（既読 IO が誤爆しないよう完了フラグを遅延）
  useEffect(() => {
    if (!shotId) {
      setIsCommentListScrollComplete(false);
      return;
    }
    if (allComments.length === 0) {
      setIsCommentListScrollComplete(true);
      return;
    }

    if (isFirstCommentScrollForShotRef.current) {
      isFirstCommentScrollForShotRef.current = false;
      let readyTimer: ReturnType<typeof setTimeout> | null = null;
      const t = setTimeout(() => {
        const scrollEl = commentListScrollRef.current;
        if (scrollEl) {
          scrollEl.scrollTop = scrollEl.scrollHeight;
        }
        readyTimer = setTimeout(() => setIsCommentListScrollComplete(true), 400);
      }, 100);
      return () => {
        clearTimeout(t);
        if (readyTimer) clearTimeout(readyTimer);
      };
    }

    setIsCommentListScrollComplete(true);
  }, [shotId, allComments.length]);

  // SP: コメントオーバーレイ表示時に一覧を最下部へ
  // 過去コメントの追加読み込み中は閲覧位置を維持したいので最下部へ飛ばさない。
  useEffect(() => {
    if (isDesktop || !showMobileComments) return;
    if (commentPrependAnchorRef.current > 0) return;
    // ディープリンクで特定コメントへスクロール中は最下部へ飛ばさない
    if (deepLinkAutoScrollLockRef.current) return;
    const rafId = requestAnimationFrame(() => {
      const el = commentListScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(rafId);
  }, [isDesktop, showMobileComments, filteredComments.length]);

  // ===== コメント一覧の追加読み込み（無限スクロール）=====
  // 一覧は古い→新しい順で描画されるため、上端に近づいたら過去ページを取得して先頭へ差し込む。
  const loadMoreComments = useCallback(async () => {
    if (!shotId || !hasMoreComments || isLoadingMoreComments) return;

    setIsLoadingMoreComments(true);
    try {
      const result = await fetchMoreComments({
        shot_id: shotId,
        filter: 'all',
        include_replies: true,
        limit: COMMENT_PAGE_LIMIT,
        offset: nextCommentOffset,
      }).unwrap();

      const page = result?.data;
      if (!page?.comments?.length) {
        setHasMoreComments(false);
        return;
      }

      setHasMoreComments(page.has_next);
      setNextCommentOffset(page.next_offset || 0);
      setAllComments(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const older = page.comments.filter(c => !existingIds.has(c.id));
        if (older.length === 0) return prev;
        return sortCommentsByDate([...older, ...prev]);
      });
    } catch (error) {
      console.error('[loadMoreComments] 過去コメントの取得エラー:', error);
    } finally {
      setIsLoadingMoreComments(false);
    }
  }, [shotId, hasMoreComments, isLoadingMoreComments, nextCommentOffset, fetchMoreComments]);

  // 一覧を先頭ページから取り直す（WS 再接続時や差分更新に失敗したときの復旧経路）。
  // 追加読み込みで開いた分まで含めて取得し、表示中の履歴が巻き戻らないようにする。
  const refetchCommentsFromStart = useCallback(async () => {
    if (!shotId) return;

    const limit = Math.min(
      Math.max(allCommentsRef.current.length, COMMENT_PAGE_LIMIT),
      COMMENT_MAX_PAGE_LIMIT,
    );

    try {
      const result = await fetchMoreComments(
        { shot_id: shotId, filter: 'all', include_replies: true, limit, offset: 0 },
        false,
      ).unwrap();

      const page = result?.data;
      if (!page?.comments) return;

      setAllComments(sortCommentsByDate(page.comments));
      setHasMoreComments(page.has_next);
      setNextCommentOffset(page.next_offset || 0);
    } catch (error) {
      console.warn('[Preview] コメント一覧の取り直しに失敗:', error);
    }
  }, [shotId, fetchMoreComments]);

  const handleCommentListScroll = useCallback(() => {
    const el = commentListScrollRef.current;
    if (!el || isCommentScrollThrottledRef.current) return;

    isCommentScrollThrottledRef.current = true;

    if (
      isCommentListScrollComplete &&
      el.scrollTop < COMMENT_LOAD_MORE_THRESHOLD_PX &&
      hasMoreComments &&
      !isLoadingMoreComments
    ) {
      commentPrependAnchorRef.current = el.scrollHeight;
      loadMoreComments();
    }

    setTimeout(() => {
      isCommentScrollThrottledRef.current = false;
    }, COMMENT_SCROLL_THROTTLE_MS);
  }, [isCommentListScrollComplete, hasMoreComments, isLoadingMoreComments, loadMoreComments]);

  // スクロール監視。DesktopCommentList / MobileCommentList は排他的にマウントされるため、
  // 参照先が入れ替わる isDesktop / showMobileComments も依存に含めて張り替える。
  useEffect(() => {
    const el = commentListScrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleCommentListScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleCommentListScroll);
  }, [handleCommentListScroll, isDesktop, showMobileComments]);

  // 過去コメントを先頭へ差し込んだ後、増えた分だけ scrollTop を足して閲覧位置を維持する。
  // 取得が空振り／失敗して一覧が伸びなかった場合も、ここでアンカーを解除する。
  useEffect(() => {
    if (isLoadingMoreComments) return;

    const anchorHeight = commentPrependAnchorRef.current;
    if (anchorHeight === 0) return;
    commentPrependAnchorRef.current = 0;

    const el = commentListScrollRef.current;
    if (!el) return;
    const grownHeight = el.scrollHeight - anchorHeight;
    if (grownHeight > 0) {
      el.scrollTop += grownHeight;
    }
  }, [allComments, isLoadingMoreComments]);

  // フィルタ適用後の一覧がビューポートを埋めていない場合は続けて過去ページを読む。
  // （「完了」フィルタのように該当コメントが古い位置に偏るケースで空表示にならないようにする）
  useEffect(() => {
    const el = commentListScrollRef.current;
    if (!el || !isCommentListScrollComplete) return;
    if (!hasMoreComments || isLoadingMoreComments) return;
    // clientHeight 0（レイアウト確定前）は「埋まっていない」と誤判定して連続取得に
    // なるため対象外にする。
    if (el.clientHeight === 0 || el.scrollHeight > el.clientHeight) return;

    commentPrependAnchorRef.current = el.scrollHeight;
    loadMoreComments();
  }, [
    filteredComments,
    isCommentListScrollComplete,
    hasMoreComments,
    isLoadingMoreComments,
    loadMoreComments,
  ]);

  // コメント既読: 表示領域に入った相手メッセージをバッチ登録（トークルームと同様）
  useEffect(() => {
    if (
      !isCommentListScrollComplete ||
      currentUserData?.data?.id === undefined ||
      !shotId ||
      !canObserveShotCommentReads
    ) {
      return;
    }

    const root = commentListScrollRef.current;
    if (!root) return;

    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
    }

    intersectionObserverRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const commentId = entry.target.getAttribute('data-message-id');
          if (!commentId) return;
          const comment = allCommentsRef.current.find(c => c.id === commentId);
          if (comment && isShotCommentUnread(comment)) {
            addShotCommentToReadQueue(commentId);
          }
        });
      },
      {
        root,
        rootMargin: '0px',
        threshold: 0.5,
      },
    );

    commentElementRefs.current.forEach(el => {
      intersectionObserverRef.current?.observe(el);
    });

    return () => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }
    };
  }, [
    isCommentListScrollComplete,
    currentUserData?.data?.id,
    shotId,
    canObserveShotCommentReads,
    filteredComments.length,
    isShotCommentUnread,
    addShotCommentToReadQueue,
  ]);

  // Esc で閉じる（SPコメントオーバーレイ開時はオーバーレイを先に閉じる）
  const handleEscKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      // MA タイムライン編集中: 選択解除のみ（プレビューは閉じない）
      if (maTimelineEdit.isEditing) {
        maTimelineEdit.selectClip(null);
        return;
      }
      if (showMobileComments) {
        setShowMobileComments(false);
      } else {
        handleClose();
      }
    }
  }, [handleClose, showMobileComments, maTimelineEdit]);

  // アップロード完了時に自動クリア。
  // エラー行が残っている場合は自動クリアしない（再試行ボタン付きで表示し続け、
  // 失敗ダイアログの選択で解決する）
  useEffect(() => {
    if (uploadingFiles.length === 0) return;
    if (uploadingFiles.every(f => f.status === 'completed')) {
      const timer = setTimeout(() => clearCompleted(), 1500);
      return () => clearTimeout(timer);
    }
  }, [uploadingFiles, clearCompleted]);

  // スクロール位置からアクティブなバリエーションを算出するヘルパー
  const syncVariationFromScroll = useCallback(
    (scrollContainer: HTMLElement, slideRefs: (HTMLDivElement | null)[]) => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;
      let bestIdx = -1;
      let bestDist = Infinity;

      slideRefs.forEach((ref, idx) => {
        if (!ref) return;
        const rect = ref.getBoundingClientRect();
        const slideCenter = rect.top + rect.height / 2;
        const dist = Math.abs(slideCenter - containerCenter);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = idx;
        }
      });

      if (bestIdx !== -1) {
        setCurrentVariation(bestIdx);
      }
    },
    [],
  );

  // PC: scroll + rAF スロットルでスクロール位置 → currentVariation 同期。
  // 旧実装の IntersectionObserver(intersectionRatio) はスライドがスクロール領域より
  // 高い場合（max-height が効かない波形オーディオ等）に閾値へ届かず検出漏れし、
  // scrollend も scroll-snap proximity で発火が不安定だった。スクロールしている限り
  // 必ず発火する scroll イベントで、位置ベースの syncVariationFromScroll を直接駆動する。
  useEffect(() => {
    if (!isDesktop || previewSlides.length === 0) return;
    const scroller = centerColumnRef.current;
    if (!scroller) return;

    let rafId: number | null = null;
    const sync = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncVariationFromScroll(scroller, variationSlideRefs.current);
      });
    };
    scroller.addEventListener('scroll', sync, { passive: true });

    // 波形オーディオ等の遅延ロードでスライド高さが後から変わると、
    // スクロール位置と currentVariation がずれる（特にページを開いた直後の初回スクロール）。
    // スライドのサイズ変化を監視し、レイアウト確定時に再同期する。
    const resizeObserver = new ResizeObserver(sync);
    variationSlideRefs.current.forEach(ref => {
      if (ref) resizeObserver.observe(ref);
    });

    sync();

    return () => {
      scroller.removeEventListener('scroll', sync);
      resizeObserver.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isDesktop, previewSlides.length, syncVariationFromScroll]);

  // SP: scroll + rAF スロットルでスクロール位置 → currentVariation 同期（PC と同方式）
  useEffect(() => {
    if (isDesktop || previewSlides.length === 0) return;
    const scroller = mobileScrollerRef.current;
    if (!scroller) return;

    let rafId: number | null = null;
    const sync = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncVariationFromScroll(scroller, mobileCardRefs.current);
      });
    };
    scroller.addEventListener('scroll', sync, { passive: true });

    // 波形オーディオ等の遅延ロードによるスライド高さ変化に追従して再同期（PC と同方式）
    const resizeObserver = new ResizeObserver(sync);
    mobileCardRefs.current.forEach(ref => {
      if (ref) resizeObserver.observe(ref);
    });

    sync();

    return () => {
      scroller.removeEventListener('scroll', sync);
      resizeObserver.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isDesktop, previewSlides.length, syncVariationFromScroll]);

  // 自動再生: バリエーション/テイク切替時に再生状態を引き継ぐ
  // - ユーザーが一度も再生操作していない → 自動再生しない
  // - 前のメディアが再生中だった → 新しいメディアを自動再生
  // - 前のメディアが停止中だった → 停止状態を維持
  //
  // NOTE: mediaRefs / playingStates のキー。MA スライドは同一動画を複数セグメントで
  // 参照できるため、メディア ID ではなくスライドキー（ma-N）で引く。
  const currentMediaItemId = currentSlide?.kind === 'ma'
    ? slideKey(currentSlide)
    : currentMediaItem?.id ?? null;

  // タイムラインパネルへ渡す再生ハンドル取得関数
  const getTimelineMediaHandle = useCallback(
    (): MediaPreviewHandle | null =>
      currentMediaItemId ? mediaRefs.current[currentMediaItemId] ?? null : null,
    [currentMediaItemId],
  );

  // ===== ブラウザ実測 fps の取り込み =====
  // BE の frame_rate が未解析の動画は、表示中プレイヤーの実測 fps
  // （HLS マニフェスト / requestVideoFrameCallback）をポーリングして補完する。
  // 確定するとポーリングは停止し、タイムコード換算・ピン配置へ反映される。
  const currentProbedFps = currentMediaItem ? probedFpsById[currentMediaItem.id] : undefined;
  useEffect(() => {
    if (!currentMediaItem || currentMediaItem.file_type !== 'video') return;
    if (currentMediaItem.frame_rate && currentMediaItem.frame_rate > 0) return;
    if (currentProbedFps || !currentMediaItemId) return;

    const itemId = currentMediaItem.id;
    const refKey = currentMediaItemId;
    const timer = setInterval(() => {
      const fps = mediaRefs.current[refKey]?.getFps?.();
      if (fps && fps > 0) {
        setProbedFpsById(prev => (prev[itemId] === fps ? prev : { ...prev, [itemId]: fps }));
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [currentMediaItem, currentMediaItemId, currentProbedFps]);

  // ===== スペースキー再生ターゲット（「最後にアクティブなオーディオ」）との統合 =====
  // 現在スライドのメディアをターゲットとして登録する。コメントパネル内の添付
  // オーディオを再生・シークするとそちらが上書きし（useAudioTransport の自動登録）、
  // スライド切替・スライドメディアの再生開始で再びスライド側へ優先が戻る。
  const currentMediaItemIdRef = useRef<string | null>(null);
  currentMediaItemIdRef.current = currentMediaItemId;
  const slideSpaceTokenRef = useRef<symbol | null>(null);
  if (!slideSpaceTokenRef.current) slideSpaceTokenRef.current = Symbol('preview-slide-media');
  const claimSlideSpaceTarget = useCallback(() => {
    setSpacePlaybackTarget(slideSpaceTokenRef.current as symbol, () => {
      const id = currentMediaItemIdRef.current;
      if (id) mediaRefs.current[id]?.clickToggle();
    });
  }, []);
  useEffect(() => {
    if (currentMediaItemId) claimSlideSpaceTarget();
  }, [currentMediaItemId, claimSlideSpaceTarget]);
  useEffect(
    () => () => clearSpacePlaybackTarget(slideSpaceTokenRef.current as symbol),
    [],
  );

  useEffect(() => {
    if (!currentMediaItemId) return;

    // 同じメディアなら何もしない
    if (prevMediaItemIdRef.current === currentMediaItemId) return;

    // 前のメディアの再生状態を記録してから停止
    const prevId = prevMediaItemIdRef.current;
    const wasPlaying = prevId ? !!playingStates[prevId] : false;

    if (prevId) {
      const prevRef = mediaRefs.current[prevId];
      prevRef?.pause();
    }

    prevMediaItemIdRef.current = currentMediaItemId;

    // ユーザーがまだ再生操作していない場合は自動再生しない
    if (!hasUserInitiatedPlaybackRef.current) return;

    // 前のメディアが停止中だった場合は自動再生しない（再生状態を維持）
    if (!wasPlaying) return;

    // 新しいメディアを自動再生（少し遅延を入れてDOMの準備を待つ）
    const timer = setTimeout(() => {
      const ref = mediaRefs.current[currentMediaItemId];
      if (ref) {
        ref.play().catch(() => {
          // 自動再生がブロックされた場合は無視（ブラウザポリシー）
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [currentMediaItemId, playingStates]);

  const toggleCommentsPanel = useCallback(() => {
    // デスクトップではパネル幅を即時切替し、代わりに中央メディアを FLIP
    // （旧位置 → 新位置へ transform で滑空）させる。Home ⇄ Preview の
    // 共有エレメント遷移と同じ質感の動きにするため。
    // オーディオ単体スライドはパネル閉でプレビュー自体が畳まれて
    // タイムラインに置き換わる（動かす対象が消える）ため従来の開閉のまま。
    const isPlainAudioSlide =
      currentSlide?.kind === 'single' && currentMediaItem?.file_type === 'audio';
    panelFlipRef.current = null;
    if (isDesktop && !isPlainAudioSlide) {
      const slideEl = variationSlideRefs.current[currentVariation];
      const flipBox = slideEl ? resolveMediaFlipBox(slideEl) : null;
      if (flipBox) {
        panelFlipRef.current = {
          el: flipBox,
          firstVisualRect: captureMediaVisualRect(flipBox),
        };
        setPanelInstant(true);
      }
    }
    setShowComments(prev => !prev);
  }, [isDesktop, currentSlide, currentMediaItem, currentVariation]);

  // パネル開閉のレイアウト確定直後（paint 前）に FLIP を開始する。
  // 逆変換で旧位置に固定してから滑空させるため、切替のちらつきは出ない。
  useLayoutEffect(() => {
    const flip = panelFlipRef.current;
    if (!flip) return;
    panelFlipRef.current = null;
    runMediaLayoutFlip({
      ...flip,
      onSettled: () => setPanelInstant(false),
    });
  }, [showComments]);

  // ===== 境界線ドラッグリサイズ =====
  const handleDividerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDraggingDivider(true);

      const onMouseMove = (ev: MouseEvent) => {
        const newWidth = window.innerWidth - ev.clientX;
        const maxRight = Math.round((window.innerWidth - LEFT_SIDEBAR_WIDTH) * MAX_RIGHT_PANEL_RATIO);
        setRightPanelWidth(Math.max(MIN_RIGHT_PANEL, Math.min(maxRight, newWidth)));
      };

      const onMouseUp = () => {
        setIsDraggingDivider(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [],
  );
  const handleFilterModeChange = useCallback((mode: string) => setFilterMode(mode as FilterMode), []);
  const handleSearchTextChange = useCallback((text: string) => setSearchText(text), []);

  useEffect(() => {
    if (filterMode === "search") return;
    if (availableValues.includes(filterMode)) return;
    setFilterMode(firstAvailableMode);
  }, [filterMode, availableValues, firstAvailableMode]);

  // MediaPreview の ref 登録（ComparisonSlide 等の子コンポーネント用）
  const registerMediaRef = useCallback((mediaItemId: string, el: MediaPreviewHandle | null) => {
    mediaRefs.current[mediaItemId] = el;
  }, []);

  // メディア再生状態変更ハンドラ（各 MediaPreview の onPlayStateChange 用）
  const handleMediaPlayStateChange = useCallback((mediaId: string, playing: boolean) => {
    setPlayingStates(prev => ({ ...prev, [mediaId]: playing }));
    if (playing) {
      hasUserInitiatedPlaybackRef.current = true;
      dispatch(setAudioEnabled(true));
      // スライドメディアが再生を始めたら、スペースキー再生の優先をスライド側へ戻す
      // （コメントパネルの添付オーディオ操作後でも、動画クリック等で再びスライド優先に）
      claimSlideSpaceTarget();
    }
  }, [dispatch, claimSlideSpaceTarget]);

  // テイク切り替え
  const handleTakeChange = useCallback((varIdx: number, takeIdx: number) => {
    setTakeIndices(prev => ({ ...prev, [varIdx]: takeIdx }));
  }, []);

  // サイドバークリック → 全メディア停止 → スナップスクロール
  const navigateToVariation = useCallback((idx: number) => {
    Object.values(mediaRefs.current).forEach(ref => ref?.pause());
    setCurrentVariation(idx);
    variationSlideRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // テイクカルーセルのスクロールイベント（SP・デスクトップ共通）
  const handleTakeCarouselScroll = useCallback(
    (varIdx: number) => (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const slideWidth = el.clientWidth;
      if (slideWidth === 0) return;
      const takeIdx = Math.round(el.scrollLeft / slideWidth);
      setTakeIndices(prev => {
        if (prev[varIdx] === takeIdx) return prev;
        return { ...prev, [varIdx]: takeIdx };
      });
    },
    [],
  );

  // デスクトップ テイクカルーセルのプログラム的スクロール
  const scrollDesktopTakeCarousel = useCallback((varIdx: number, takeIdx: number) => {
    const carousel = desktopTakeCarouselRefs.current[varIdx];
    if (!carousel) return;
    const slideWidth = carousel.clientWidth;
    carousel.scrollTo({ left: slideWidth * takeIdx, behavior: 'smooth' });
  }, []);

  // キーボードナビゲーション（PC専用・フォーカス位置に依存しない）
  const handleGlobalKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' ||
        (e.target as HTMLElement)?.isContentEditable) return;

    const count = previewSlides.length;
    if (count === 0) return;

    // ===== MA タイムライン編集モードのキー操作 =====
    if (maTimelineEdit.isEditing) {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // 編集中はスライド移動しない（編集対象の不一致防止）
        e.preventDefault();
        return;
      }
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        maTimelineEdit.selectedClipId
      ) {
        e.preventDefault();
        maTimelineEdit.deleteSelected();
        return;
      }
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && maTimelineEdit.selectedClipId) {
        // 選択クリップのナッジ移動: ←→ で 1 フレーム、SHIFT+←→ で 1 秒
        e.preventDefault();
        const direction = e.key === 'ArrowRight' ? 1 : -1;
        maTimelineEdit.nudgeSelected(e.shiftKey ? direction : direction / currentMediaFps);
        return;
      }
      // 選択なしの ←→ / Space 等は通常のタイムライン操作へフォールスルー
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIdx = currentVariation + (e.key === 'ArrowUp' ? -1 : 1);
      if (nextIdx < 0 || nextIdx >= count) return;
      navigateToVariation(nextIdx);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();

      // テイク切替（従来の ←→ 動作。ALT+←→ / シーク不能メディアの ←→ で使う）
      const switchTake = () => {
        const slide = previewSlides[currentVariation];
        const group = slide?.kind === 'single' ? slide.group : undefined;
        if (!group) return;
        const currentTake = takeIndices[currentVariation] || 0;

        if (e.key === 'ArrowLeft') {
          if (group.takes.length <= 1 || currentTake === 0) {
            if (!currentMediaItemId) return;
            const ref = mediaRefs.current[currentMediaItemId];
            if (ref) {
              ref.seekTo(0);
              ref.play().catch(() => {});
            }
          } else {
            const nextTake = currentTake - 1;
            handleTakeChange(currentVariation, nextTake);
            scrollDesktopTakeCarousel(currentVariation, nextTake);
          }
        } else {
          if (group.takes.length <= 1) return;
          const nextTake = currentTake + 1;
          if (nextTake >= group.takes.length) return;
          handleTakeChange(currentVariation, nextTake);
          scrollDesktopTakeCarousel(currentVariation, nextTake);
        }
      };

      // ALT(Option)+←→: テイク切替
      if (e.altKey) {
        switchTake();
        return;
      }

      // ←→ で 1 フレーム移動、SHIFT+←→ で 1 秒移動
      //（通常プレビュー / タイムラインモード共通。再生中は一時停止してからステップする）
      const slide = previewSlides[currentVariation];
      const seekable =
        (slide?.kind === 'single' || slide?.kind === 'ma') &&
        !!currentMediaItem &&
        isAudioOrVideoType(currentMediaItem.file_type);
      if (seekable) {
        const ref = currentMediaItemId ? mediaRefs.current[currentMediaItemId] : null;
        if (!ref) return;
        const direction = e.key === 'ArrowRight' ? 1 : -1;
        const delta = e.shiftKey ? direction : direction / currentMediaFps;
        ref.pause();
        const duration = ref.getDuration();
        const max = Number.isFinite(duration) && duration > 0 ? duration : Infinity;
        const next = quantizeToFrame(
          Math.max(0, Math.min(max, ref.getCurrentTime() + delta)),
          currentMediaFps,
        );
        ref.seekTo(next);
        return;
      }

      // シーク不能メディア（画像・PDF 等）は従来どおり ←→ でテイク切替
      switchTake();
    } else if (e.key === ' ' || e.key === 'k' || e.key === 'j' || e.key === 'l') {
      // spacePlayback のグローバルリスナーが先に処理した場合は二重トグルを防ぐ
      if (e.defaultPrevented) return;
      e.preventDefault();
      if (e.key === ' ' || e.key === 'k') {
        // 「最後にアクティブなオーディオ」（コメントパネルの添付音源を含む）を優先。
        // 通常は現在スライドのメディアが登録されている
        if (toggleSpacePlaybackTarget()) return;
      }
      if (!currentMediaItemId) return;
      const ref = mediaRefs.current[currentMediaItemId];
      if (!ref) return;
      if (e.key === ' ' || e.key === 'k') {
        ref.clickToggle();
      } else {
        ref.seek(e.key === 'j' ? -5 : 5);
      }
    } else if (
      e.code === 'KeyM' && e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey
    ) {
      // SHIFT+M: 現在の再生位置でコメント入力欄へ一発移動。
      // 通常モードはピンボタンと同じくタイムコード（[M:SS]）を本文へ挿入、
      // タイムラインモードはフォーカスのみ（入力開始時にタイムコードが凍結される）。
      e.preventDefault();
      if (isDesktop) {
        // コメントパネルが閉じていて（かつタイムラインモードでもない場合）は先に開く。
        // CommentField は常時マウントされているため ref は即座に使える。
        if (!timelineMode && !showComments) {
          setShowComments(true);
        }
      } else if (!showMobileComments) {
        setShowMobileComments(true);
      }
      commentFieldHandleRef.current?.focusComposer({ withPin: true });
    } else if (
      e.code === 'KeyC' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey
    ) {
      // C: コメントパネル開閉（PC は閉じるとタイムラインモード、SP はオーバーレイ開閉）
      if (e.repeat) return;
      e.preventDefault();
      if (isDesktop) {
        toggleCommentsPanel();
      } else {
        setShowMobileComments(prev => !prev);
      }
    }
  }, [previewSlides, currentVariation, takeIndices, currentMediaItemId, currentMediaItem, navigateToVariation, handleTakeChange, scrollDesktopTakeCarousel, timelineMode, currentMediaFps, isDesktop, showComments, showMobileComments, maTimelineEdit, toggleCommentsPanel]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      handleEscKeyDown(e);
      handleGlobalKeyDown(e);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleEscKeyDown, handleGlobalKeyDown]);

  // ピン（戻り値: テキスト先頭に挿入する [M:SS] 文字列 or undefined）
  const handlePinClick = useCallback((): string | void => {
    if (pinData) {
      // タイムラインモード中はピン常時 ON（OFF トグル不可）。
      // タイムコードの再取得は CommentField 側のチップ操作で行う。
      if (timelineMode) return;
      setPinData(null);
      return;
    }
    if (!currentMediaItem) return;

    setPinData({ media_item_id: currentMediaItem.id });

    // audio/video の場合、現在の再生位置を [M:SS]（タイムラインモード中は [M:SS.FF]）形式で返す
    if (isAudioOrVideoType(currentMediaItem.file_type)) {
      const ref = currentMediaItemId ? mediaRefs.current[currentMediaItemId] : null;
      if (ref) {
        return formatBracketTimecode(ref.getCurrentTime(), {
          fps: currentMediaFps,
          withFrames: timelineMode,
        });
      }
    }
  }, [pinData, currentMediaItem, currentMediaItemId, currentMediaFps, timelineMode]);

  // ===== タイムラインモード: ピン常時 ON =====
  // タイムラインモード中はコメントのピンを常に現在のメディアへ紐付ける
  // （メディア切替にも追従）。既存コメントの編集中は handleEdit が復元した
  // ピン状態を優先し、編集終了後にここで再度 ON へ戻る。
  useEffect(() => {
    if (!timelineMode || !currentMediaItem || commentToEdit) return;
    setPinData(prev =>
      prev?.media_item_id === currentMediaItem.id
        ? prev
        : { media_item_id: currentMediaItem.id },
    );
  }, [timelineMode, currentMediaItem, commentToEdit]);

  // タイムラインモードを抜けたら自動 ON にしたピンを既定（OFF）へ戻す。
  // 編集中は復元済みピンを壊さないよう据え置く（保存/キャンセル時に解消される）。
  const prevTimelineModeRef = useRef(timelineMode);
  useEffect(() => {
    if (prevTimelineModeRef.current && !timelineMode && !commentToEdit) {
      setPinData(null);
    }
    prevTimelineModeRef.current = timelineMode;
  }, [timelineMode, commentToEdit]);

  /**
   * タイムラインモードの CommentField に渡すライブタイムコード取得関数。
   * CommentField 側が一定間隔でポーリングし、再生位置に追従する
   * `[M:SS.FF]` チップを表示する（入力開始で凍結・送信時に本文へ自動付与）。
   */
  const getTimelineLiveTimecode = useCallback((): string | null => {
    if (!currentMediaItemId) return null;
    const ref = mediaRefs.current[currentMediaItemId];
    if (!ref) return null;
    return formatBracketTimecode(ref.getCurrentTime(), {
      fps: currentMediaFps,
      withFrames: true,
    });
  }, [currentMediaItemId, currentMediaFps]);

  // ===== コメントピン & タイムスタンプ =====

  /** variationGroups 内からメディアアイテムを ID で検索 */
  const findMediaItem = useCallback(
    (mediaItemId: string): MediaItem | undefined =>
      variationGroups.flatMap(g => g.takes).find(t => t.id === mediaItemId),
    [variationGroups],
  );

  /**
   * メディアアイテム ID → 再生ハンドル（mediaRefs）のキー解決。
   * MA スライドで表示中の動画は、メディア ID ではなくスライドキー（ma-N）で
   * 登録されているため、MA スライドのキーへ変換する。
   * 同じ動画を複数の MA スライドが使う場合、コメントはメディア ID にしか
   * 紐づかないため、表示中のスライドを優先し、なければ最初の一致を使う。
   */
  const resolveMediaRefKey = useCallback(
    (mediaItemId: string): string => {
      if (currentSlide?.kind === 'ma' && currentSlide.segment.videoItem.id === mediaItemId) {
        return slideKey(currentSlide);
      }
      const maSlide = previewSlides.find(
        s => s.kind === 'ma' && s.segment.videoItem.id === mediaItemId,
      );
      return maSlide ? slideKey(maSlide) : mediaItemId;
    },
    [previewSlides, currentSlide],
  );

  /** 指定メディアアイテムまでナビゲーション（スライド+テイク切替 & スクロール） */
  const navigateToMediaItem = useCallback((mediaItemId: string): boolean => {
    for (let varIdx = 0; varIdx < previewSlides.length; varIdx++) {
      const slide = previewSlides[varIdx];
      if (slide.kind === 'single') {
        const takeIdx = slide.group.takes.findIndex(t => t.id === mediaItemId);
        if (takeIdx !== -1) {
          setCurrentVariation(varIdx);
          setTakeIndices(prev => ({ ...prev, [varIdx]: takeIdx }));
          variationSlideRefs.current[varIdx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
      } else if (slide.kind === 'ma') {
        if (slide.segment.videoItem.id === mediaItemId) {
          setCurrentVariation(varIdx);
          variationSlideRefs.current[varIdx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
      } else if (slide.entries.some(e => e.group.takes.some(t => t.id === mediaItemId))) {
        setCurrentVariation(varIdx);
        variationSlideRefs.current[varIdx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
    }
    return false;
  }, [previewSlides]);

  /** 指定メディアの特定秒数にシーク＆再生（ナビゲーション直後は遅延、表示済みなら即時） */
  const seekAndPlay = useCallback((mediaItemId: string, seconds: number | null, needsDelay: boolean) => {
    const doSeek = () => {
      const ref = mediaRefs.current[resolveMediaRefKey(mediaItemId)];
      if (ref) {
        if (seconds != null) {
          ref.seekTo(seconds);
        }
        ref.play().catch(() => {});
      }
    };
    needsDelay ? setTimeout(doSeek, 400) : doSeek();
  }, [resolveMediaRefKey]);

  /** 再生アイコンクリック: 再生/停止トグル（同じコメントなら停止、異なれば再生） */
  const handleCommentPinClick = useCallback((messageId: string, mediaItemId: string, timecodeStart: string) => {
    if (playingCommentId === messageId) {
      const ref = mediaRefs.current[resolveMediaRefKey(mediaItemId)];
      if (ref) ref.pause();
      setPlayingCommentId(null);
      return;
    }

    const item = findMediaItem(mediaItemId);
    if (!item) return;

    const isCurrentMedia = currentMediaItem?.id === mediaItemId;
    if (!isCurrentMedia) navigateToMediaItem(mediaItemId);

    if (isAudioOrVideoType(item.file_type)) {
      seekAndPlay(mediaItemId, parseTimecode(timecodeStart, getMediaFps(item, probedFpsById[item.id])), !isCurrentMedia);
      setPlayingCommentId(messageId);
    }
  }, [playingCommentId, findMediaItem, navigateToMediaItem, currentMediaItem, seekAndPlay, resolveMediaRefKey, probedFpsById]);

  /**
   * コメント内 [M:SS] / [M:SS.FF] / [N]（絶対フレーム）リンククリック:
   * ピン付きならメディア移動→シーク、ピンなしなら現在メディアでシーク。
   * frames はメディアごとの fps で秒に換算する（[N] は seconds = 0 + 絶対フレーム）。
   */
  const handleTimestampClick = useCallback((seconds: number, mediaItemId: string | null, frames?: number | null) => {
    const resolveTime = (item: MediaItem) =>
      frames != null ? seconds + frames / getMediaFps(item, probedFpsById[item.id]) : seconds;

    if (mediaItemId) {
      const item = findMediaItem(mediaItemId);
      if (!item || !isAudioOrVideoType(item.file_type)) return;

      const isCurrentMedia = currentMediaItem?.id === mediaItemId;
      if (!isCurrentMedia) navigateToMediaItem(mediaItemId);
      seekAndPlay(mediaItemId, resolveTime(item), !isCurrentMedia);
    } else if (currentMediaItem && isAudioOrVideoType(currentMediaItem.file_type)) {
      seekAndPlay(currentMediaItem.id, resolveTime(currentMediaItem), false);
    }
  }, [findMediaItem, currentMediaItem, navigateToMediaItem, seekAndPlay, probedFpsById]);

  // ===== コメント送信の共通後処理 =====
  // 送信したコメントを一覧へ反映し、ショットステータスを更新する。
  // WS の created は送信者自身に配信されないため、送信レスポンスの作成済みコメントを
  // ここで反映しないと自分の投稿（特に返信）が一覧に載らない。
  const syncAfterCommentSent = useCallback(async (sendResult: SendShotCommentResponse) => {
    if (!shotId) return;

    const created = extractDetailEntity<ShotComment>(sendResult);
    if (created) {
      setAllComments(prev => {
        if (prev.some(c => c.id === created.id)) return prev;
        return sortCommentsByDate([...prev, created]);
      });
    } else {
      // レスポンス形状が想定外だったときは一覧を取り直して取りこぼしを防ぐ。
      console.warn('[syncAfterCommentSent] 送信レスポンスの形状が想定外:', sendResult);
      await refetchCommentsFromStart();
    }

    // ショットステータスをコメント送信者のロールに基づいて更新
    // CLIENT/REVIEWER → PRODUCER_PENDING(3)、それ以外（PRODUCER/DIRECTOR/ADMIN）→ CLIENT_PENDING(4)
    if (projectId && shotId && userPosition) {
      const newStatus =
        (userPosition === 'CLIENT' || userPosition === 'REVIEWER')
          ? SHOT_STATUS.PRODUCER_PENDING   // 3: プロデューサー待ち
          : SHOT_STATUS.CLIENT_PENDING;    // 4: クライアント待ち

      try {
        await updateShotStatus({
          project_id: projectId,
          shot_id: shotId,
          status: newStatus,
        }).unwrap();
      } catch (statusError) {
        console.error('[syncAfterCommentSent] ショットステータス更新エラー:', statusError);
      }
    }
  }, [shotId, projectId, userPosition, refetchCommentsFromStart, updateShotStatus]);

  // ===== コメント送信 =====
  const handleSendMessage = useCallback(
    async (messageText: string, meta?: AiAssistSendMeta) => {
      const hasMessage = messageText.trim() !== '';
      const hasFiles = selectedFiles.length > 0;
      const hasFilesWithPath = selectedFilesWithPath.length > 0;

      if (!shotId || (!hasMessage && !hasFiles && !hasFilesWithPath) || isSending || isUploading) return;

      // 編集モード
      if (commentToEdit) {
        if (!hasMessage && editExistingFiles.length === 0 && !hasFiles && !hasFilesWithPath) return;
        try {
          // 新規ファイルをアップロード
          let newFileKeys: string[] = [];
          if (hasFiles || hasFilesWithPath) {
            const normalFilesWithPath: FileWithPath[] = selectedFiles.map(file => ({
              file,
              relativePath: file.name,
            }));
            const allFilesToUpload: FileWithPath[] = [...normalFilesWithPath, ...selectedFilesWithPath];
            setSelectedFiles([]);
            setSelectedFilesWithPath([]);

            const uploadResults = await uploadFiles(allFilesToUpload);
            // 失敗があれば「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログで解決
            const hadFailures = uploadResults.some(f => f.status === 'error');
            const resolved = await resolveUploadFailures(uploadResults);
            if (resolved === null) {
              // キャンセル: 進捗を畳み、添付を選択状態へ戻して更新を中断
              clearCompleted();
              setSelectedFiles(selectedFiles);
              setSelectedFilesWithPath(selectedFilesWithPath);
              return false;
            }
            if (hadFailures) clearCompleted();
            newFileKeys = resolved
              .filter(f => f.fileKey)
              .map(f => f.fileKey as string);
          }

          // file_keys の計算（BE は「置換」セマンティクス）
          // ファイルに変更がなければ undefined（省略 = 変更なし）
          // 変更があれば、残存する既存ファイルの file_key + 新規アップロードの file_key を結合
          let fileKeysToSend: string[] | undefined;
          if (editFilesChanged || newFileKeys.length > 0) {
            const keptFiles = commentToEdit.files
              .filter(f => editExistingFiles.some(ef => ef.id === f.id));
            const existingFileKeys = keptFiles
              .map(f => f.file_key || extractS3KeyFromUrl(f.original_url))
              .filter((k): k is string => !!k);
            fileKeysToSend = [...existingFileKeys, ...newFileKeys];
          }

          const doUpdate = () => updateComment({
            shot_id: shotId,
            comment_id: commentToEdit.id,
            comment: messageText.trim(),
            media_item_id: pinData?.media_item_id ?? null,
            file_keys: fileKeysToSend,
            ...(meta?.original_comment !== undefined && { original_comment: meta.original_comment }),
            ...(meta?.ai_comment !== undefined && { ai_comment: meta.ai_comment }),
            ...(meta?.active_variant !== undefined && { active_variant: meta.active_variant }),
          }).unwrap();
          // 添付をアップロードした場合、反映が終わるまでは離脱を引き止める
          const result = newFileKeys.length > 0
            ? await withPostProcess(doUpdate, 'コメントを更新中')
            : await doUpdate();
          if (result.data?.comment) {
            setAllComments(prev =>
              prev.map(c =>
                c.id === commentToEdit.id ? mergeShotCommentFromPatch(c, result.data.comment) : c,
              ),
            );
          }
          setCommentToEdit(null);
          setPinData(null);
          setEditExistingFiles([]);
          setEditFilesChanged(false);
        } catch (error) {
          console.error('[handleSendMessage] コメント更新エラー:', error);
        }
        return;
      }

      // ファイルキーを収集
      let fileKeys: string[] = [];
      if (hasFiles || hasFilesWithPath) {
        try {
          const normalFilesWithPath: FileWithPath[] = selectedFiles.map(file => ({
            file,
            relativePath: file.name,
          }));
          const allFilesToUpload: FileWithPath[] = [...normalFilesWithPath, ...selectedFilesWithPath];
          setSelectedFiles([]);
          setSelectedFilesWithPath([]);

          const uploadResults = await uploadFiles(allFilesToUpload);
          // 失敗があれば「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログで解決
          const hadFailures = uploadResults.some(f => f.status === 'error');
          const resolved = await resolveUploadFailures(uploadResults);
          if (resolved === null) {
            // キャンセル: 進捗を畳み、添付を選択状態へ戻して送信を中断
            //（本文は CommentField 側が復元する）
            clearCompleted();
            setSelectedFiles(selectedFiles);
            setSelectedFilesWithPath(selectedFilesWithPath);
            return false;
          }
          if (hadFailures) clearCompleted();
          fileKeys = resolved
            .filter(f => f.fileKey)
            .map(f => f.fileKey as string);

          if (fileKeys.length === 0 && !hasMessage) {
            console.warn('[handleSendMessage] アップロードに失敗、メッセージもないため送信スキップ');
            return;
          }
        } catch (error) {
          console.error('[handleSendMessage] ファイルアップロードエラー:', error);
          if (!hasMessage) return;
        }
      }

      try {
        const doSend = () => sendComment({
          shot_id: shotId,
          comment: hasMessage ? messageText.trim() : '',
          parent_id: commentToReply?.id || null,
          file_keys: fileKeys.length > 0 ? fileKeys : undefined,
          media_item_id: pinData?.media_item_id || null,
          ...(meta?.original_comment !== undefined && { original_comment: meta.original_comment }),
          ...(meta?.ai_comment !== undefined && { ai_comment: meta.ai_comment }),
          ...(meta?.active_variant !== undefined && { active_variant: meta.active_variant }),
        }).unwrap();
        // 添付をアップロードした場合、送信が終わるまでは離脱を引き止める
        const sendResult = fileKeys.length > 0
          ? await withPostProcess(doSend, 'コメントを送信中')
          : await doSend();

        if (commentToReply) setCommentToReply(null);
        // タイムラインモード中はピン常時 ON のため解除しない（次のコメントにも
        // 現在メディアの紐付けとタイムコード自動付与を継続する）。
        if (pinData && !timelineMode) setPinData(null);

        await syncAfterCommentSent(sendResult);
      } catch (error) {
        console.error('[handleSendMessage] コメント送信エラー:', error);
      }
    },
    [
      shotId, isSending, isUploading, commentToEdit, commentToReply,
      selectedFiles, selectedFilesWithPath, pinData, timelineMode,
      editExistingFiles, editFilesChanged,
      sendComment, updateComment, uploadFiles,
      resolveUploadFailures, clearCompleted, withPostProcess,
      syncAfterCommentSent,
    ],
  );

  // ===== 比較グループからのコメント送信 =====
  // コメント入力 or メディア選択のいずれかがあれば送信可能。
  // コメント未入力でメディアのみ選択時は自動テキストを生成して送信する。
  const handleComparisonSend = useCallback(
    async (baseName: string, entries: ComparisonEntry[], messageText: string) => {
      if (!shotId || isSending) return;

      const selectedId = comparisonSelections[baseName] ?? null;
      const selectedEntry = selectedId
        ? entries.find(e => e.group.latestTake.id === selectedId)
        : undefined;

      // コメント入力があればそれを、なければ選択の自動テキスト（どちらもなければ送信しない）
      const autoText = selectedEntry ? `${selectedEntry.label}を選択` : '';
      const comment = messageText.trim() || autoText;
      if (!comment) return;

      try {
        const sendResult = await sendComment({
          shot_id: shotId,
          comment,
          parent_id: null,
          media_item_id: selectedEntry ? selectedEntry.group.latestTake.id : null,
        }).unwrap();

        // 送信後は選択をクリア
        setComparisonSelections(prev => ({ ...prev, [baseName]: null }));

        await syncAfterCommentSent(sendResult);
      } catch (error) {
        console.error('[handleComparisonSend] コメント送信エラー:', error);
      }
    },
    [shotId, isSending, comparisonSelections, sendComment, syncAfterCommentSent],
  );

  // 比較グループのピン: 選択中メディア（未選択なら先頭エントリを選択）の
  // 再生位置 [M:SS] を返してテキスト先頭に挿入する（右パネルのピンと同挙動）。
  const handleComparisonPinClick = useCallback(
    (baseName: string, entries: ComparisonEntry[]): string | void => {
      const selectedId = comparisonSelections[baseName] ?? null;
      const entry = selectedId
        ? entries.find(e => e.group.latestTake.id === selectedId)
        : entries[0];
      if (!entry) return;

      const item = entry.group.latestTake;
      if (!selectedId) {
        setComparisonSelections(prev => ({ ...prev, [baseName]: item.id }));
      }

      if (isAudioOrVideoType(item.file_type)) {
        const ref = mediaRefs.current[item.id];
        if (ref) {
          return formatBracketTimecode(ref.getCurrentTime());
        }
      }
    },
    [comparisonSelections],
  );

  // ===== メディアタグのワンクリック切替 =====
  const editableTagSide: MediaTagSide | null = isProducerUser
    ? 'producer'
    : isClientUser
      ? 'client'
      : null;

  const handleTagToggle = useCallback(
    (item: MediaItem, side: MediaTagSide, tag: MediaItemProducerTag | MediaItemClientTag) => {
      if (!shotId || side !== editableTagSide) return;
      if (side === 'producer') {
        const next = item.producer_tag === tag ? null : (tag as MediaItemProducerTag);
        updateMediaItemTags({ shot_id: shotId, media_item_id: item.id, producer_tag: next });
      } else {
        const next = item.client_tag === tag ? null : (tag as MediaItemClientTag);
        updateMediaItemTags({ shot_id: shotId, media_item_id: item.id, client_tag: next });
      }
    },
    [shotId, editableTagSide, updateMediaItemTags],
  );

  // ===== 返信・編集・解決・削除ハンドラー =====
  const handleReply = useCallback(
    (message: MessageData) => {
      const c = allComments.find(x => x.id === message.id);
      if (c) setCommentToReply(c);
    },
    [allComments],
  );

  const handleEdit = useCallback(
    (message: MessageData) => {
      const c = allComments.find(x => x.id === message.id);
      if (c) {
        setCommentToEdit(c);
        setPinData(c.media_item_id ? { media_item_id: c.media_item_id } : null);
        setEditExistingFiles(
          c.files.map(f => ({ id: f.id, fileName: f.file_name, fileType: f.file_type })),
        );
        setEditFilesChanged(false);
      }
    },
    [allComments],
  );

  // 解決アニメーション制御
  const { fadingOutEntry: commentFadingOutEntry, executeWithAnimation: executeCommentAnimation } = useAnimatedMessageResolve();

  const handleResolve = useCallback(
    (message: MessageData) => {
      if (!shotId) return;

      const direction = message.is_resolved ? "left" : "right";
      let apiResult: { comment: ShotComment } | null = null;

      executeCommentAnimation(
        message.id,
        direction,
        async () => {
          const result = await updateComment({
            shot_id: shotId,
            comment_id: message.id,
            is_resolved: !message.is_resolved,
          }).unwrap();
          if (result.data?.comment) {
            apiResult = result.data;
          }
        },
        async () => {
          if (!apiResult) return;

          let updatedComments: ShotComment[] = [];
          flushSync(() => {
            setAllComments(prev => {
              updatedComments = prev.map(c =>
                c.id === message.id ? mergeShotCommentFromPatch(c, apiResult!.comment) : c,
              );
              return updatedComments;
            });
          });

          if (projectId && shotId && updatedComments.length > 0) {
            const latestUnresolved = [...updatedComments]
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
              .find(c => !c.is_resolved);

            if (latestUnresolved) {
              const pos = latestUnresolved.author?.position;
              const newStatus =
                (pos === 'CLIENT' || pos === 'REVIEWER')
                  ? SHOT_STATUS.PRODUCER_PENDING
                  : pos
                    ? SHOT_STATUS.CLIENT_PENDING
                    : null;

              if (newStatus !== null) {
                try {
                  await updateShotStatus({
                    project_id: projectId,
                    shot_id: shotId,
                    status: newStatus,
                  }).unwrap();
                } catch (statusError) {
                  console.error('[handleResolve] ショットステータス更新エラー:', statusError);
                }
              }
            } else if (userPosition) {
              const newStatus =
                (userPosition === 'CLIENT' || userPosition === 'REVIEWER')
                  ? SHOT_STATUS.PRODUCER_PENDING
                  : SHOT_STATUS.CLIENT_PENDING;

              try {
                await updateShotStatus({
                  project_id: projectId,
                  shot_id: shotId,
                  status: newStatus,
                }).unwrap();
              } catch (statusError) {
                console.error('[handleResolve] ショットステータス更新エラー:', statusError);
              }
            }
          }
        },
      );
    },
    [shotId, updateComment, projectId, updateShotStatus, userPosition, executeCommentAnimation],
  );

  // タイムラインピンのポップオーバーからの解決済み / 進行中トグル。
  // コメント一覧と同じ handleResolve を通し、ショットステータス連動も揃える
  const handlePinResolveToggle = useCallback(
    (pin: TimelinePin) => {
      const comment = allComments.find(c => c.id === pin.commentId);
      if (comment) handleResolve(shotCommentToMessageData(comment));
    },
    [allComments, handleResolve],
  );

  const handleDelete = useCallback(
    (message: MessageData) => {
      const c = allComments.find(x => x.id === message.id);
      if (c) setCommentToDelete(c);
    },
    [allComments],
  );

  const confirmDelete = useCallback(async () => {
    if (!commentToDelete || !shotId || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteComment({ shot_id: shotId, comment_id: commentToDelete.id }).unwrap();
      setAllComments(prev => prev.filter(c => c.id !== commentToDelete.id));
      setCommentToDelete(null);
    } catch (error) {
      console.error('[confirmDelete] エラー:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [commentToDelete, shotId, isDeleting, deleteComment]);

  const cancelDelete = useCallback(() => setCommentToDelete(null), []);
  const cancelEdit = useCallback(() => {
    setCommentToEdit(null);
    setPinData(null);
    setEditExistingFiles([]);
    setEditFilesChanged(false);
  }, []);
  const cancelReply = useCallback(() => setCommentToReply(null), []);

  const handleRemoveExistingFile = useCallback((fileId: string) => {
    setEditExistingFiles(prev => prev.filter(f => f.id !== fileId));
    setEditFilesChanged(true);
  }, []);

  // ファイルダウンロード記録（楽観的更新: UI即反映 → API送信）
  // ダウンロード既読の API 送信（一括ダウンロード時の連続呼び出しを配列 1 回の PATCH にまとめる）
  const enqueueMarkDownloaded = useBatchedCall<string>(async (fileIds) => {
    if (!shotId) return;
    try {
      await markCommentFilesAsDownloaded({
        shot_id: shotId,
        downloaded_file_ids: fileIds,
      }).unwrap();
    } catch (error) {
      console.warn('[handleFileDownload] ダウンロード記録エラー:', error);
    }
  });

  const handleFileDownload = useCallback((commentId: string, fileId: string) => {
    if (!shotId || !currentUserData?.data) return;

    const user = currentUserData.data;
    const comment = allCommentsRef.current.find(c => c.id === commentId);

    // 既にダウンロード済みならスキップ
    const targetFile = comment?.files.find(f => f.id === fileId);
    if (targetFile?.downloaded_users?.some(u => u.id === user.id)) return;

    // receivers にある自分のデータ（BE由来のアバターURL）を優先使用
    const receiverSelf = comment?.receivers.find(r => r.id === user.id);
    const avatarUrl = receiverSelf?.avatar ?? user.icon ?? null;

    // 楽観的更新（即座にUIに反映）
    setAllComments(prev => prev.map(c => {
      if (c.id !== commentId) return c;
      return {
        ...c,
        files: c.files.map(file => {
          if (file.id !== fileId) return file;
          return {
            ...file,
            downloaded_users: [
              ...(file.downloaded_users || []),
              {
                id: user.id,
                display_name: user.stage_name || user.fullname || 'Unknown',
                avatar: avatarUrl,
                downloaded_at: new Date().toISOString(),
              },
            ],
          };
        }),
      };
    }));

    enqueueMarkDownloaded(fileId);
  }, [shotId, currentUserData, enqueueMarkDownloaded]);

  // ===== ファイルハンドラー =====
  const handleFilesSelected = useCallback((files: File[]) => {
    setSelectedFiles(prev => [...prev, ...files]);
  }, []);

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (index < selectedFiles.length) {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
      } else {
        const folderIdx = index - selectedFiles.length;
        setSelectedFilesWithPath(prev => prev.filter((_, i) => i !== folderIdx));
      }
    },
    [selectedFiles.length],
  );

  const handleFileDrop = useCallback((files: File[]) => {
    if (files.length === 0) return;
    setSelectedFiles(prev => [...prev, ...files]);
  }, []);

  const handleFileDropWithPath = useCallback((filesWithPath: FileWithPath[]) => {
    if (filesWithPath.length === 0) return;
    setSelectedFilesWithPath(prev => [...prev, ...filesWithPath]);
  }, []);

  const allSelectedFiles = useMemo(() => {
    return [...selectedFiles, ...selectedFilesWithPath.map(f => f.file)];
  }, [selectedFiles, selectedFilesWithPath]);

  const handleUploadCancel = useCallback((fileId: string) => abortFile(fileId), [abortFile]);
  const handleUploadClear = useCallback(() => clearCompleted(), [clearCompleted]);

  // ===== WebSocket リアルタイム連携 =====
  //
  // 他ユーザーのショットコメント送信/編集/削除/既読/ダウンロード既読を `/ws/spa/events`
  // 経由で受信し、allComments を差分更新する。BE 仕様書「Messenger リアルタイム連携」準拠。
  const { sendEvent } = useMessengerSocket();

  const handleWsEvent = useCallback(
    (ev: MessengerIncomingEvent) => {
      if (!shotId) return;
      switch (ev.type) {
        case 'message.shot_comment.created':
        case 'message.shot_comment.updated': {
          const { comment_id } = ev.payload;
          fetchCommentDetail({ shot_id: shotId, comment_id }, false)
            .unwrap()
            .then((res) => {
              const fetched = extractDetailEntity<ShotComment>(res);
              if (!fetched) {
                console.warn(
                  '[Preview:WS] detail response shape unexpected',
                  res,
                );
                refetchCommentsFromStart();
                return;
              }
              setAllComments((prev) => {
                const exists = prev.some((c) => c.id === fetched.id);
                if (exists) {
                  return prev.map((c) => (c.id === fetched.id ? fetched : c));
                }
                return sortCommentsByDate([...prev, fetched]);
              });
            })
            .catch((err) => {
              console.warn(
                '[Preview:WS] fetchCommentDetail failed, falling back to list refetch',
                err,
              );
              refetchCommentsFromStart();
            });
          break;
        }
        case 'message.shot_comment.deleted': {
          const { comment_id } = ev.payload;
          setAllComments((prev) => prev.filter((c) => c.id !== comment_id));
          break;
        }
        case 'message.media_item.updated': {
          // 他ユーザーによるタグ変更を RTK Query キャッシュへ反映
          const { media_item_id, producer_tag, client_tag } = ev.payload;
          (dispatch as any)(
            mediaItemsApi.util.updateQueryData('getMediaItems', { shot_id: shotId }, (draft) => {
              const item = draft.data?.media_items?.find((m) => m.id === media_item_id);
              if (!item) return;
              item.producer_tag = (producer_tag ?? null) as MediaItem['producer_tag'];
              item.client_tag = (client_tag ?? null) as MediaItem['client_tag'];
            }),
          );
          break;
        }
        case 'message.shot_comment.read': {
          const { comment_ids, reader_user_id } = ev.payload;
          if (!comment_ids?.length) break;
          const idSet = new Set(comment_ids);
          setAllComments((prev) =>
            prev.map((c) => {
              if (!idSet.has(c.id)) return c;
              if (c.receivers.some((r) => r.id === reader_user_id)) return c;
              const knownAuthor =
                c.author?.id === reader_user_id ? c.author : undefined;
              const knownReceiver = c.receivers.find((r) => r.id === reader_user_id);
              const user: ShotCommentUser = knownReceiver ?? knownAuthor ?? {
                id: reader_user_id,
                display_name: '',
                avatar: null,
              };
              return {
                ...c,
                receivers: [
                  ...c.receivers,
                  { ...user, seen_at: new Date().toISOString() },
                ],
              };
            }),
          );
          break;
        }
        case 'message.shot_comment.download_read': {
          const { comment_ids, file_ids, reader_user_id } = ev.payload;
          if (!comment_ids?.length || !file_ids?.length) break;
          const cIdSet = new Set(comment_ids);
          const fIdSet = new Set(file_ids);
          setAllComments((prev) =>
            prev.map((c) => {
              if (!cIdSet.has(c.id)) return c;
              return {
                ...c,
                files: c.files.map((file) => {
                  if (!fIdSet.has(file.id)) return file;
                  if (file.downloaded_users?.some((u) => u.id === reader_user_id))
                    return file;
                  const knownReceiver = c.receivers.find(
                    (r) => r.id === reader_user_id,
                  );
                  return {
                    ...file,
                    downloaded_users: [
                      ...(file.downloaded_users || []),
                      {
                        id: reader_user_id,
                        display_name: knownReceiver?.display_name ?? '',
                        avatar: knownReceiver?.avatar ?? null,
                        downloaded_at: new Date().toISOString(),
                      },
                    ],
                  };
                }),
              };
            }),
          );
          break;
        }
        default:
          break;
      }
    },
    [shotId, fetchCommentDetail, refetchCommentsFromStart, dispatch],
  );

  useMessengerSubscribe('shot_comment', shotId, handleWsEvent);

  // allComments 内の author / receivers / downloaded_users を Redux userDirectory に
  // 集約し、WS 経由の read / download_read でユーザー未知でもアイコンを表示可能にする。
  const directoryUsers = useMemo(
    () => collectMessengerUsers(allComments),
    [allComments],
  );
  useSyncUserDirectory(directoryUsers);

  useMessengerReconnect(refetchCommentsFromStart);

  // Typing 送信（scope: shot_comment）。
  const { onTypingChange, onTypingCommit } = useTypingSender({
    send: sendEvent,
    scope: 'shot_comment',
    ids: {
      project_id: projectId || '',
      shot_id: shotId || '',
      parent_id: commentToReply?.id ?? null,
    },
    enabled: Boolean(projectId && shotId),
  });

  const resolveTypingUserName = useCallback(
    (userId: number): string | undefined => {
      if (currentUserId === userId && currentUserForReceivers) {
        return currentUserForReceivers.display_name;
      }
      const fromComment = allCommentsRef.current.find(
        (c) => c.author?.id === userId,
      )?.author;
      if (fromComment?.display_name) return fromComment.display_name;
      const fromReceiver = allCommentsRef.current
        .flatMap((c) => c.receivers)
        .find((r) => r.id === userId);
      return fromReceiver?.display_name;
    },
    [currentUserId, currentUserForReceivers],
  );

  // ===== Render Helpers =====

  /** 再生アイコン用ツールチップの生成（"M:SS バリエーション名" 形式） */
  const getPinTooltip = useCallback((comment: ShotComment): string => {
    if (!comment.media_item_id) return '';
    const parts: string[] = [];
    if (comment.timecode_start) parts.push(comment.timecode_start);
    const group = variationGroups.find(g => g.takes.some(t => t.id === comment.media_item_id));
    if (group) parts.push(group.variationName);
    return parts.join(' ') || '';
  }, [variationGroups]);

  const { copy: copyCommentLink } = useCopyToClipboard({
    successMessage: 'リンクをコピーしました',
  });

  /**
   * 三点メニュー「リンクをコピー」: コメントへのディープリンクを Markdown
   * リンク（`[ショット名: コメント冒頭](URL)`）でコピーする。貼り付け先では
   * SPA 内遷移のチップになり、開くと該当コメントへスクロール（ピン付きなら
   * ピン先メディアを再生）する。
   */
  const handleCopyCommentLink = useCallback((message: MessageData) => {
    if (!projectId || !shotId) return;
    const head = message.message.replace(/\s+/g, ' ').trim().slice(0, 20);
    const label = head ? `${shotName}: ${head}` : shotName;
    void copyCommentLink(
      toMarkdownLink(label, buildShotCommentLink(projectId, shotId, message.id)),
    );
  }, [projectId, shotId, shotName, copyCommentLink]);

  // ===== コメントのディープリンク（?comment=）適用 =====

  /** 一覧に無いコメントを単体取得して差し込む（WS 差分更新と同じ経路） */
  const fetchAndInjectComment = useCallback(async (commentId: string): Promise<ShotComment | null> => {
    if (!shotId) return null;
    try {
      const res = await fetchCommentDetail({ shot_id: shotId, comment_id: commentId }, false).unwrap();
      const fetched = extractDetailEntity<ShotComment>(res);
      if (!fetched) return null;
      setAllComments(prev =>
        prev.some(c => c.id === fetched.id)
          ? prev.map(c => (c.id === fetched.id ? fetched : c))
          : sortCommentsByDate([...prev, fetched]),
      );
      return fetched;
    } catch {
      return null;
    }
  }, [shotId, fetchCommentDetail]);

  /** 対象コメントが見えるようフィルタを合わせ、コメントパネルを開く */
  const revealCommentForDeepLink = useCallback((comment: ShotComment) => {
    setFilterMode(prev => {
      if (comment.is_resolved) return 'completed';
      return prev === 'completed' ? 'in_progress' : prev;
    });
    if (isDesktop) {
      setShowComments(true);
    } else {
      setShowMobileComments(true);
    }
  }, [isDesktop]);

  const scrollToCommentElement = useCallback((commentId: string): boolean => {
    const el = commentElementRefs.current.get(commentId);
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  /** ピン付きコメントをピン位置から再生（タイムラインピンと同じタイムコード解決順） */
  const playPinnedComment = useCallback((comment: ShotComment) => {
    if (!comment.media_item_id) return;
    const timecode = hasTimecode(comment.comment)
      ? extractFirstTimecode(comment.comment)
      : comment.timecode_start || '0:00';
    handleCommentPinClick(comment.id, comment.media_item_id, timecode);
  }, [handleCommentPinClick]);

  const { highlightedCommentId } = useShotCommentDeepLink({
    shotId,
    commentsReady: isCommentListScrollComplete,
    allComments,
    fetchAndInjectComment,
    revealComment: revealCommentForDeepLink,
    scrollToComment: scrollToCommentElement,
    playPinnedComment,
    autoScrollLockRef: deepLinkAutoScrollLockRef,
  });

  // ⌥[ / ⌥]: 遷移元リスト（やること / 完了タブ）の前後のショットへ移動
  usePreviewQueueShortcut(projectId, shotId);

  /** コメント 1 件の描画 */
  const renderCommentItem = useCallback(
    (comment: ShotComment, index: number, array: ShotComment[]) => {
      const messageData = shotCommentToMessageData(comment);
      const isOurs = isMessageOnRight(comment);
      const prevComment = array[index - 1];
      const isSameAuthorAsPrev = !!prevComment && prevComment.author?.id === comment.author?.id;
      const parentComment = comment.parent_id
        ? allComments.find(c => c.id === comment.parent_id) || null
        : null;
      const parentMessage = parentComment ? shotCommentToMessageData(parentComment) : null;
      const isFadingOut = commentFadingOutEntry?.id === comment.id;
      const fadeClass = isFadingOut
        ? commentFadingOutEntry.direction === "right" ? "fade-out-right" : "fade-out-left"
        : undefined;
      const highlightClass =
        highlightedCommentId === comment.id ? "deep-link-highlight" : undefined;
      const wrapperClass =
        [fadeClass, highlightClass].filter(Boolean).join(" ") || undefined;

      return (
        <CommentMotionItem
          key={comment.id}
          layout
          transition={{ layout: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } }}
          data-message-id={comment.id}
          ref={el => registerCommentElementRef(comment.id, el)}
          className={wrapperClass}
          style={isFadingOut ? { pointerEvents: "none" as const } : undefined}
        >
          <MessageItem
            message={messageData}
            isOurs={isOurs}
            showAvatar={!isSameAuthorAsPrev}
            showActions={true}
            parentMessage={parentMessage}
            currentUserId={currentUserId}
            onReply={handleReply}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onResolve={handleResolve}
            onCopyLink={handleCopyCommentLink}
            onPinClick={handleCommentPinClick}
            onTimestampClick={handleTimestampClick}
            pinTooltip={getPinTooltip(comment)}
            playingMessageId={playingCommentId}
            onFileDownload={handleFileDownload}
            filterMode={filterMode === "search" ? "in_progress" : filterMode} // 検索中は進行中と同じ表示
          />
        </CommentMotionItem>
      );
    },
    [allComments, currentUserId, isMessageOnRight, handleReply, handleEdit, handleDelete, handleResolve, handleCopyCommentLink, handleCommentPinClick, handleTimestampClick, getPinTooltip, playingCommentId, handleFileDownload, registerCommentElementRef, commentFadingOutEntry, filterMode, highlightedCommentId],
  );

  /** CommentField の共通描画 */
  const renderCommentField = () => (
    <div className="u-col u-w100">
      <div className="u-w100 u-row-center">
        <TypingIndicator
          scope="shot_comment"
          scopeKey={shotId}
          currentUserId={currentUserId}
          resolveUserName={resolveTypingUserName}
        />
      </div>
      <CommentField
        ref={attachCommentFieldHandle}
        showBottomAppBar={false}
        // タイムラインモード中はピン常時 ON（固定）のためアイコン自体を隠し、
        // タイムコードチップだけを操作点として残す。
        showPinIcon={!timelineMode}
        onPinClick={handlePinClick}
        isPinActive={!!pinData}
        getLiveTimecode={timelineMode ? getTimelineLiveTimecode : undefined}
        onSend={handleSendMessage}
        editMode={!!commentToEdit}
        initialValue={commentToEdit?.comment || ''}
        onCancel={commentToEdit ? cancelEdit : cancelReply}
        replyMode={!!commentToReply}
        replyToMessage={
          commentToReply
            ? {
                id: commentToReply.id,
                author: { display_name: commentToReply.author.display_name },
                message: commentToReply.comment,
              }
            : null
        }
        onFilesSelected={handleFilesSelected}
        onFilesDroppedWithPath={handleFileDropWithPath}
        selectedFiles={allSelectedFiles}
        onRemoveFile={handleRemoveFile}
        multipleFiles={true}
        pinTooltip={currentSlideName ? `${currentSlideName}にコメント` : ''}
        showIconsInEditMode={true}
        existingFiles={commentToEdit ? editExistingFiles : undefined}
        onRemoveExistingFile={commentToEdit ? handleRemoveExistingFile : undefined}
        placeholder="コメントを送信"
        onTypingChange={onTypingChange}
        onTypingCommit={onTypingCommit}
        enableChikuchiku={isClientUser}
        chikuchikuScene="chikuchiku_filter"
        chikuchikuContext={{
          project_id: projectId,
          shot_id: shotId,
          exclude_comment_id: commentToEdit?.id ?? null,
        }}
        enableReplyAssistant={isProducerUser}
        replyAssistantScene="reply_assistant_shot"
        replyAssistantContext={{
          project_id: projectId,
          shot_id: shotId,
          exclude_comment_id: commentToEdit?.id ?? null,
        }}
        initialProofread={commentToEdit?.proofread ?? null}
      />
    </div>
  );

  // CommentField を一覧にオーバーレイするため、最下部コメントが隠れないよう
  // CommentField 実高さ + 余白 を list の padding-bottom に確保する。
  const commentListBottomPadding = Math.max(
    COMMENT_LIST_BOTTOM_PADDING_MIN,
    commentFieldHeight + COMMENT_LIST_BOTTOM_PADDING_BUFFER,
  );

  /** アクションアイコン行（コメントトグルは SP のみ。PC は右パネルヘッダー側に配置） */
  const renderActionIcons = (fabSize: number = 48) => (
    <>
      {(fabVariant === 'client' || isApproved) && (
        <CircularFab
          isActive={isApproved}
          onClick={handleApprovalWithTransition}
          disabled={fabVariant === 'client' ? (!canApprove || isApprovalLoading) : isApprovalLoading}
          variant={fabVariant}
          size={fabSize}
        />
      )}
      {!isDesktop && (
        <CommentIconWrapper onClick={() => setShowMobileComments(true)}>
          <IconButton style={{ fontSize: 32 }} icon="chat_bubble_outline" />
          <span className="label8">{commentCount > 0 ? commentCount : 'コメント'}</span>
        </CommentIconWrapper>
      )}
    </>
  );

  /** 右パネル開閉トグル（PC: パネル開時はヘッダー左上、閉時は中央カラム右上） */
  const renderPanelToggle = () => (
    <ToolTip
      description={showComments ? 'コメントパネルを閉じる（C）' : 'コメントパネルを開く（C）'}
      placement="bottom"
    >
      <IconButton
        icon={showComments ? 'right_panel_close' : 'right_panel_open'}
        onClick={toggleCommentsPanel}
        ariaLabel={showComments ? 'コメントパネルを閉じる' : 'コメントパネルを開く'}
      />
    </ToolTip>
  );

  // ===== Loading =====
  // maState.isResolving: シーケンスファイル（RPP / CSV）があるショットは
  // 紐付けの解決が終わるまでローディングに留め、最初から MA 表示で開始する
  //（個別アイテム一覧 → MA スライドへの切替ちらつきを防ぐ。取得失敗時は
  // isResolving が false に落ちて従来の個別表示へフォールバックする）
  if (isLoadingMediaItems || isLoadingComments || maState.isResolving) {
    return createPortal(
      <ModalOverlay $background={projectAmbient?.background}>
        {projectAmbient && (
          <ProjectAmbientBackground
            variant="container"
            accents={projectAmbient.accents}
          />
        )}
        <div className="u-col-center" style={{ height: '100dvh' }}>
          {!arrivedViaSharedTransition && <SvgLoadingAnimation />}
        </div>
      </ModalOverlay>,
      document.body,
    );
  }

  if (mediaItemsError || commentsError) {
    console.error('Error loading data:', { mediaItemsError, commentsError });
  }

  // ======================
  //  Desktop Render
  // ======================
  const renderDesktop = () => (
    <DropZoneOverlay
      enabled={true}
      onDrop={handleFileDrop}
      overlayText="ファイルまたはフォルダをドロップしてアップロード"
      dragActiveText="ここにドロップ"
      enableFolderDrop={true}
      onDropWithPath={handleFileDropWithPath}
    >
    <DesktopLayout>
      {/* ---- 左サイドバー ---- */}
      <LeftSidebar>
        <SidebarHeader>
          <ToolTip description="戻る（Esc）" placement="right">
            <IconButton onClick={handleClose} icon="chevron_left" />
          </ToolTip>
        </SidebarHeader>
        <SidebarThumbScroller ref={thumbScrollerRef}>
        <SidebarThumbList>
        {sidebarDndEnabled ? (
          <DndContext sensors={sidebarSensors} collisionDetection={closestCenter} onDragEnd={handleSidebarDragEnd}>
            <SortableContext items={sidebarSortableIds} strategy={verticalListSortingStrategy}>
              {previewSlides.map((slide, idx) => {
                const thumbTake = slideThumbTake(slide);
                return (
                  <SortableSidebarThumb
                    key={slideKey(slide)}
                    id={thumbTake.id}
                    isActive={currentVariation === idx}
                    tooltipText={slideTooltip(slide)}
                    thumbnailSrc={thumbTake.thumbnail || getMediaUrl(thumbTake)}
                    onClick={() => navigateToVariation(idx)}
                    mediaPreviewProps={sidebarThumbPreviewProps(thumbTake)}
                    size={sidebarThumbSize}
                    badgeCount={slideEntryCount(slide)}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        ) : (
          previewSlides.map((slide, idx) => {
            const thumbTake = slideThumbTake(slide);
            return (
              <StaticSidebarThumb
                key={slideKey(slide)}
                isActive={currentVariation === idx}
                tooltipText={slideTooltip(slide)}
                thumbnailSrc={thumbTake.thumbnail || getMediaUrl(thumbTake)}
                onClick={() => navigateToVariation(idx)}
                mediaPreviewProps={sidebarThumbPreviewProps(thumbTake)}
                size={sidebarThumbSize}
                badgeCount={slideEntryCount(slide)}
              />
            );
          })
        )}
        </SidebarThumbList>
        </SidebarThumbScroller>
        {canEditShot && (
          <SidebarFooter>
            <ToolTip description="ショット編集" placement="right" theme="sidebar-thumb">
              <IconButton
                icon="home_storage"
                size={24}
                ariaLabel="ショット編集"
                onClick={() => setShowShotEditModal(true)}
              />
            </ToolTip>
          </SidebarFooter>
        )}
      </LeftSidebar>

      {/* ---- 中央カラム ---- */}
      <CenterColumn>
        {/* パネル閉時の再オープン導線（ヘッダーごと畳まれるため中央カラム側に出す） */}
        <PanelOpenFloating $visible={!showComments}>{renderPanelToggle()}</PanelOpenFloating>
        <ScrollerWrap $collapsed={timelineAudioFull}>
        <CenterColumnScroller ref={centerColumnRef}>
          {previewSlides.map((slide, varIdx) => {
            if (slide.kind === 'ma') {
              const refKey = slideKey(slide);
              // 編集中はワーキングコピー（バリアント切替・レイアウト適用済み）、
              // 非編集中はプレビューのオプション切替を適用したセグメントを再生
              const maSegmentForSlide =
                maTimelineEdit.editedSegment &&
                maTimelineEdit.editingSegmentIndex === slide.segmentIndex
                  ? maTimelineEdit.editedSegment
                  : maPreviewSegments?.get(slide.segmentIndex) ?? slide.segment;
              return (
                <VariationSlide
                  key={refKey}
                  ref={el => {
                    variationSlideRefs.current[varIdx] = el;
                  }}
                >
                  <MediaContainer ref={varIdx === 0 ? mediaContainerRef : undefined}>
                    <MediaOverlay
                      {...{ [PREVIEW_MEDIA_FLIP_ATTR]: '' }}
                      {...(varIdx === 0 ? { [PREVIEW_MEDIA_LANDING_ATTR]: '' } : {})}
                    >
                      <MaSlide
                        // 動画バリアント切替時は key で再マウントして src を確実に差し替える
                        key={`${refKey}:${maSegmentForSlide.videoItem.id}`}
                        ref={(el: MediaPreviewHandle | null) => {
                          mediaRefs.current[refKey] = el;
                        }}
                        segment={maSegmentForSlide}
                        active={currentVariation === varIdx}
                        sequence={maSequence ?? undefined}
                        segmentIndex={slide.segmentIndex}
                        timeChartBaseName={maTimeChartBaseName}
                        sizeMode={desktopSizeMode}
                        style={{ maxWidth: DESKTOP_MEDIA_MAX_WIDTH }}
                        mediaPreviewProps={maDesktopSlideMediaProps}
                        onPlayStateChange={(playing: boolean) => handleMediaPlayStateChange(refKey, playing)}
                      />
                    </MediaOverlay>
                  </MediaContainer>
                </VariationSlide>
              );
            }

            if (slide.kind === 'comparison') {
              return (
                <VariationSlide
                  key={`cmp-${slide.baseName}`}
                  ref={el => {
                    variationSlideRefs.current[varIdx] = el;
                  }}
                >
                  <ComparisonSlide
                    baseName={slide.baseName}
                    entries={slide.entries.map(e => ({ label: e.label, item: e.group.latestTake }))}
                    selectedId={comparisonSelections[slide.baseName] ?? null}
                    keptId={clientKeptSelections[slide.baseName] ?? null}
                    onSelect={id => handleComparisonSelect(slide.baseName, id)}
                    registerMediaRef={registerMediaRef}
                    onPlayStateChange={handleMediaPlayStateChange}
                    mediaPreviewProps={MEDIA_PREVIEW_FULL_PROPS}
                    sizeMode={COMPARISON_SIZE_MODE_DESKTOP}
                    maxWidth={DESKTOP_MEDIA_MAX_WIDTH}
                    editableTagSide={editableTagSide}
                    onTagToggle={handleTagToggle}
                    onSend={text => handleComparisonSend(slide.baseName, slide.entries, text)}
                    isSending={isSending}
                    onPinClick={() => handleComparisonPinClick(slide.baseName, slide.entries)}
                    isPinActive={!!(comparisonSelections[slide.baseName] ?? null)}
                  />
                </VariationSlide>
              );
            }

            const group = slide.group;
            const tIdx = takeIndices[varIdx] || 0;
            const take = group.takes[tIdx] || group.latestTake;
            const hasTakes = group.takes.length > 1;

            return (
              <VariationSlide
                key={group.latestTake.id}
                ref={el => {
                  variationSlideRefs.current[varIdx] = el;
                }}
              >
                {/* メディアプレビュー + テイクカルーセル */}
                <MediaContainer ref={varIdx === 0 ? mediaContainerRef : undefined}>
                  {hasTakes ? (
                    <>
                      <DesktopTakeCarousel
                        ref={el => { desktopTakeCarouselRefs.current[varIdx] = el; }}
                        onScroll={handleTakeCarouselScroll(varIdx)}
                      >
                        {group.takes.map((t, ti) => (
                          <DesktopTakeSlide key={t.id}>
                            {/* HOME 進行中ホイールからの共有エレメント遷移の
                                着地目標（最初のスライドのみ） */}
                            <MediaOverlay
                              {...{ [PREVIEW_MEDIA_FLIP_ATTR]: '' }}
                              {...(varIdx === 0 && ti === 0
                                ? { [PREVIEW_MEDIA_LANDING_ATTR]: '' }
                                : {})}
                            >
                              <TakeLabel take={group.takes.length - tIdx} isHiding={hiddenBadges[group.takes[tIdx]?.id]} />
                              <MediaPreview
                                ref={(el: MediaPreviewHandle | null) => {
                                  mediaRefs.current[t.id] = el;
                                }}
                                src={getMediaUrl(t)}
                                downloadUrl={t.original_url}
                                thumbnailSrc={t.thumbnail}
                                mediaType={toMediaType(t.file_type)}
                                sizeMode={desktopSizeMode}
                                // タイムラインモード中のオーディオはプレビュー非表示
                                // （visibility なので audio 要素は生きたまま = 再生継続）
                                style={timelineMode && t.file_type === 'audio' ? { visibility: 'hidden' as const } : undefined}
                                {...desktopSlideMediaProps}
                                waveformUrl={t.has_waveform && t.waveform_url ? t.waveform_url : undefined}
                                fileName={t.file_name}
                                fileId={t.id}
                                acrScanStatus={t.acr_scan_status}
                                acrScanResult={t.acr_scan_result}
                                acrScannedAt={t.acr_scanned_at}
                                createdAt={t.updated_at}
                                onPlayStateChange={(playing: boolean) => handleMediaPlayStateChange(t.id, playing)}
                              />
                              <DesktopTakeDotsOverlay>
                                {group.takes.map((_, di) => (
                                  <TakeDot key={di} $active={di === tIdx} onClick={() => {
                                    handleTakeChange(varIdx, di);
                                    scrollDesktopTakeCarousel(varIdx, di);
                                  }} />
                                ))}
                              </DesktopTakeDotsOverlay>
                            </MediaOverlay>
                          </DesktopTakeSlide>
                        ))}
                      </DesktopTakeCarousel>

                      {tIdx > 0 && (
                        <TakeNavBtn $side="left" onClick={() => {
                          const newIdx = tIdx - 1;
                          handleTakeChange(varIdx, newIdx);
                          scrollDesktopTakeCarousel(varIdx, newIdx);
                        }}>
                          <span className="material-symbols-rounded">chevron_left</span>
                        </TakeNavBtn>
                      )}

                      {tIdx < group.takes.length - 1 && (
                        <TakeNavBtn $side="right" onClick={() => {
                          const newIdx = tIdx + 1;
                          handleTakeChange(varIdx, newIdx);
                          scrollDesktopTakeCarousel(varIdx, newIdx);
                        }}>
                          <span className="material-symbols-rounded">chevron_right</span>
                        </TakeNavBtn>
                      )}
                    </>
                  ) : (
                    <MediaOverlay
                      {...{ [PREVIEW_MEDIA_FLIP_ATTR]: '' }}
                      {...(varIdx === 0
                        ? { [PREVIEW_MEDIA_LANDING_ATTR]: '' }
                        : {})}
                    >
                      <MediaPreview
                        ref={(el: MediaPreviewHandle | null) => {
                          mediaRefs.current[take.id] = el;
                        }}
                        src={getMediaUrl(take)}
                        downloadUrl={take.original_url}
                        thumbnailSrc={take.thumbnail}
                        mediaType={toMediaType(take.file_type)}
                        sizeMode={desktopSizeMode}
                        // タイムラインモード中のオーディオはプレビュー非表示
                        // （visibility なので audio 要素は生きたまま = 再生継続）
                        style={{
                          maxWidth: DESKTOP_MEDIA_MAX_WIDTH,
                          ...(timelineMode && take.file_type === 'audio'
                            ? { visibility: 'hidden' as const }
                            : {}),
                        }}
                        {...desktopSlideMediaProps}
                        waveformUrl={take.has_waveform && take.waveform_url ? take.waveform_url : undefined}
                        fileName={take.file_name}
                        fileId={take.id}
                        acrScanStatus={take.acr_scan_status}
                        acrScanResult={take.acr_scan_result}
                        acrScannedAt={take.acr_scanned_at}
                        createdAt={take.updated_at}
                        onPlayStateChange={(playing: boolean) => handleMediaPlayStateChange(take.id, playing)}
                      />
                    </MediaOverlay>
                  )}
                </MediaContainer>
              </VariationSlide>
            );
          })}
        </CenterColumnScroller>

        {/* 情報 + アクション行（フロート: プレビュー上に重ねて表示） */}
        <SlideInfoRow>
          <SlideInfoText>
            {currentSlide?.kind === 'ma' &&
              renderMaOptionChips(currentSlide.segmentIndex, !!playingStates[slideKey(currentSlide)])}
            <div className="heading-spacing-18 u-text-black">{currentSlideName}</div>
            <div className="bodytext-13 u-text-black" translate="no">{shotName}</div>
            <div className="bodytext-11 u-text-light-gray u-mb8">
              <DateDisplay date={currentMediaItem?.updated_at || ''} type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME} />
            </div>
            {currentSlide?.kind === 'single' && currentMediaItem && (
              <div style={{ marginBottom: '8px' }}>
                <ClientTagChips
                  item={currentMediaItem}
                  editable={editableTagSide === 'client'}
                  onToggle={(item, tag) => handleTagToggle(item, 'client', tag)}
                />
              </div>
            )}
          </SlideInfoText>
          <SlideInfoActions $timeline={timelineMode}>
            {renderActionIcons(48)}
          </SlideInfoActions>
        </SlideInfoRow>
        </ScrollerWrap>

        {/* ---- タイムラインパネル（フレーム単位チェックモード） ---- */}
        <AnimatePresence initial={false}>
        {timelineMode && currentMediaItem && currentMediaItemId && (
          <TimelineReveal
            key={timelineRevealKind}
            $fill={timelineAudioFull}
            {...TIMELINE_REVEAL_MOTION[timelineRevealKind]}
            transition={PANEL_MOTION_TRANSITION}
          >
          <TimelinePanel
            key={currentMediaItemId}
            fps={currentMediaFps}
            durationHint={
              currentMaSegment
                ? Math.max(
                    maSegmentDisplaySeconds(currentMaSegment) ?? 0,
                    currentMaSegment.clipsEndSeconds,
                  ) || currentMediaItem.duration
                : currentMediaItem.duration
            }
            isPlaying={!!playingStates[currentMediaItemId]}
            getMediaHandle={getTimelineMediaHandle}
            peaks={timelineWaveform.peaks}
            peaksLoading={timelineWaveform.isLoading}
            peaksError={timelineWaveform.error}
            tracks={currentMaSegment ? maTimelineTracks : undefined}
            fillHeight={timelineAudioFull}
            pins={timelinePins}
            onPinResolveToggle={handlePinResolveToggle}
            maEdit={
              currentSlide?.kind === 'ma'
                ? {
                    available: canEditShot && !!maState.maFileItem,
                    isEditing: maTimelineEdit.isEditing,
                    isDirty: maTimelineEdit.isDirty,
                    isSaving: maTimelineEdit.isSaving,
                    selectedClipId: maTimelineEdit.selectedClipId,
                    variantGroups: maTimelineEdit.variantGroups,
                    variantOverrides: maTimelineEdit.variantOverrides,
                    optionSlots: maTimelineEdit.optionSlots,
                    optionSelections: maTimelineEdit.optionSelections,
                    // プレビューのチップで切り替えたオプション選択（と操作順）を初期状態として編集開始
                    onEnterEdit: () =>
                      maTimelineEdit.enterEdit(maPreviewOptionSelections, maPreviewOptionOrder),
                    onCancel: maTimelineEdit.cancelEdit,
                    onSave: () => {
                      void maTimelineEdit.save().catch(() => {
                        /* エラーは handleMaCsvSave 内でトースト表示済み */
                      });
                    },
                    onSelectClip: maTimelineEdit.selectClip,
                    onTrimClip: maTimelineEdit.trimClip,
                    onAddClip: maTimelineEdit.addClip,
                    onSwitchVariant: maTimelineEdit.switchVariant,
                    onSelectOption: maTimelineEdit.selectOption,
                  }
                : null
            }
            commentField={
              // layoutId で右パネル側の入力欄と相互に位置をアニメーションさせる。
              // 高さ計測 ref はパネル側のみ（タイムライン中は一覧が非表示のため不要。
              // AnimatePresence の退場クローンが ref を null 上書きする事故も防ぐ）。
              <motion.div
                layoutId="preview-comment-field"
                layoutDependency={timelineMode}
                transition={PANEL_MOTION_TRANSITION}
                className="u-w100"
              >
                {renderCommentField()}
              </motion.div>
            }
          />
          </TimelineReveal>
        )}
        </AnimatePresence>
      </CenterColumn>

      {/* ---- ドラッグ可能な境界線 + 右パネル（閉時も残し、幅とスライドで開閉） ---- */}
      {showComments && (
        <ResizeDivider $isDragging={isDraggingDivider} onMouseDown={handleDividerMouseDown} />
      )}
      <RightPanel
        $width={rightPanelWidth}
        $isOpen={showComments}
        $instant={panelInstant}
        $dragging={isDraggingDivider}
      >
        <PanelInner $width={rightPanelWidth} $isOpen={showComments}>
          {/* フィルターバー（コメント一覧の上にフロート） */}
          <TopAppBarFloat>
            <TopAppBarHome
              options={commentFilterOptions}
              defaultValue={firstAvailableMode}
              value={filterMode}
              onChange={handleFilterModeChange}
              showSearchInput={true}
              onSearchChange={handleSearchTextChange}
              searchPlaceholder="コメントを検索..."
              leftActions={renderPanelToggle()}
            />
          </TopAppBarFloat>

          <DesktopCommentList ref={commentListScrollRef} $bottomPadding={commentListBottomPadding}>
            <LayoutGroup key={filterMode}>
              {filteredComments.map(renderCommentItem)}
            </LayoutGroup>
          </DesktopCommentList>

          {/* タイムラインモード中は CommentField をタイムラインパネル側にのみ描画する */}
          {!timelineMode && (
            <DesktopCommentFieldWrapper
              ref={commentFieldWrapperRef}
              layoutId="preview-comment-field"
              layoutDependency={timelineMode}
              transition={PANEL_MOTION_TRANSITION}
            >
              {renderCommentField()}
            </DesktopCommentFieldWrapper>
          )}
        </PanelInner>
      </RightPanel>
    </DesktopLayout>
    </DropZoneOverlay>
  );

  // ======================
  //  Mobile Render
  // ======================
  const renderMobile = () => (
    <DropZoneOverlay
      enabled={true}
      onDrop={handleFileDrop}
      overlayText="ファイルまたはフォルダをドロップしてアップロード"
      dragActiveText="ここにドロップ"
      enableFolderDrop={true}
      onDropWithPath={handleFileDropWithPath}
    >
    <MobileLayout>
      {/* ヘッダー */}
      <MobileHeader>
        <ToolTip description="戻る（Esc）" placement="right">
          <IconButton onClick={handleClose} icon="chevron_left" />
        </ToolTip>
      </MobileHeader>

      <MobileScroller ref={mobileScrollerRef}>
        {/* バリエーションカード（1画面=1メディア） */}
        {previewSlides.map((slide, varIdx) => {
          if (slide.kind === 'ma') {
            const refKey = slideKey(slide);
            // プレビューのオプション切替を適用したセグメントを再生（SP は編集モードなし）
            const maSegmentForSlide =
              maPreviewSegments?.get(slide.segmentIndex) ?? slide.segment;
            return (
              <MobileVariationSlide
                key={refKey}
                ref={el => {
                  mobileCardRefs.current[varIdx] = el;
                }}
              >
                <MobileMediaArea
                  {...(varIdx === 0 ? { [PREVIEW_MEDIA_LANDING_ATTR]: '' } : {})}
                >
                  <MaSlide
                    ref={(el: MediaPreviewHandle | null) => {
                      mediaRefs.current[refKey] = el;
                    }}
                    segment={maSegmentForSlide}
                    active={currentVariation === varIdx}
                    sequence={maSequence ?? undefined}
                    segmentIndex={slide.segmentIndex}
                    timeChartBaseName={maTimeChartBaseName}
                    sizeMode={MOBILE_SIZE_MODE}
                    mediaPreviewProps={maMobileSlideMediaProps}
                    onPlayStateChange={(playing: boolean) => handleMediaPlayStateChange(refKey, playing)}
                  />
                </MobileMediaArea>
              </MobileVariationSlide>
            );
          }

          if (slide.kind === 'comparison') {
            return (
              <MobileVariationSlide
                key={`cmp-${slide.baseName}`}
                ref={el => {
                  mobileCardRefs.current[varIdx] = el;
                }}
              >
                <MobileComparisonInner>
                  <ComparisonSlide
                    baseName={slide.baseName}
                    entries={slide.entries.map(e => ({ label: e.label, item: e.group.latestTake }))}
                    selectedId={comparisonSelections[slide.baseName] ?? null}
                    keptId={clientKeptSelections[slide.baseName] ?? null}
                    onSelect={id => handleComparisonSelect(slide.baseName, id)}
                    registerMediaRef={registerMediaRef}
                    onPlayStateChange={handleMediaPlayStateChange}
                    mediaPreviewProps={MEDIA_PREVIEW_FULL_PROPS}
                    sizeMode={MOBILE_SIZE_MODE}
                    editableTagSide={editableTagSide}
                    onTagToggle={handleTagToggle}
                    onSend={text => handleComparisonSend(slide.baseName, slide.entries, text)}
                    isSending={isSending}
                    onPinClick={() => handleComparisonPinClick(slide.baseName, slide.entries)}
                    isPinActive={!!(comparisonSelections[slide.baseName] ?? null)}
                  />
                </MobileComparisonInner>
              </MobileVariationSlide>
            );
          }

          const group = slide.group;
          const hasTakes = group.takes.length > 1;
          const tIdx = takeIndices[varIdx] || 0;

          return (
            <MobileVariationSlide
              key={group.latestTake.id}
              ref={el => {
                mobileCardRefs.current[varIdx] = el;
              }}
            >
              {/* 共有エレメント遷移の着地目標（最初のスライドのみ） */}
              <MobileMediaArea
                {...(varIdx === 0 ? { [PREVIEW_MEDIA_LANDING_ATTR]: '' } : {})}
              >
                {hasTakes && (
                  <TakeLabel take={group.takes.length - tIdx} isHiding={hiddenBadges[group.takes[tIdx]?.id]} />
                )}
                {hasTakes ? (
                  <MobileTakeCarousel onScroll={handleTakeCarouselScroll(varIdx)}>
                    {group.takes.map(take => (
                      <MobileTakeSlide key={take.id}>
                        <MediaPreview
                          ref={(el: MediaPreviewHandle | null) => {
                            mediaRefs.current[take.id] = el;
                          }}
                          src={getMediaUrl(take)}
                          downloadUrl={take.original_url}
                          thumbnailSrc={take.thumbnail}
                          mediaType={toMediaType(take.file_type)}
                          sizeMode={MOBILE_SIZE_MODE}
                          {...MEDIA_PREVIEW_FULL_PROPS}
                          waveformUrl={take.has_waveform && take.waveform_url ? take.waveform_url : undefined}
                          fileName={take.file_name}
                          fileId={take.id}
                          acrScanStatus={take.acr_scan_status}
                          acrScanResult={take.acr_scan_result}
                          acrScannedAt={take.acr_scanned_at}
                          createdAt={take.updated_at}
                          onPlayStateChange={(playing: boolean) => handleMediaPlayStateChange(take.id, playing)}
                        />
                      </MobileTakeSlide>
                    ))}
                  </MobileTakeCarousel>
                ) : (
                  <MediaPreview
                    ref={(el: MediaPreviewHandle | null) => {
                      mediaRefs.current[group.latestTake.id] = el;
                    }}
                    src={getMediaUrl(group.latestTake)}
                    downloadUrl={group.latestTake.original_url}
                    thumbnailSrc={group.latestTake.thumbnail}
                    mediaType={toMediaType(group.latestTake.file_type)}
                    sizeMode={MOBILE_SIZE_MODE}
                    {...MEDIA_PREVIEW_FULL_PROPS}
                    waveformUrl={group.latestTake.has_waveform && group.latestTake.waveform_url ? group.latestTake.waveform_url : undefined}
                    fileName={group.latestTake.file_name}
                    fileId={group.latestTake.id}
                    acrScanStatus={group.latestTake.acr_scan_status}
                    acrScanResult={group.latestTake.acr_scan_result}
                    acrScannedAt={group.latestTake.acr_scanned_at}
                    createdAt={group.latestTake.updated_at}
                    onPlayStateChange={(playing: boolean) => handleMediaPlayStateChange(group.latestTake.id, playing)}
                  />
                )}
                {hasTakes && (
                  <TakeDotsRow>
                    {group.takes.map((_, i) => (
                      <TakeDot key={i} $active={i === tIdx} />
                    ))}
                  </TakeDotsRow>
                )}
              </MobileMediaArea>
            </MobileVariationSlide>
          );
        })}
      </MobileScroller>

      {/* 情報 + アクション行（PC と同一: ショット単位で統一表示） */}
      <MobileFooterRow>
        <SlideInfoText>
          {currentSlide?.kind === 'ma' &&
            renderMaOptionChips(currentSlide.segmentIndex, !!playingStates[slideKey(currentSlide)])}
          <div className="heading-spacing-18 u-text-black">{currentSlideName}</div>
          <div className="bodytext-13 u-text-black" translate="no">{shotName}</div>
          <div className="bodytext-11 u-text-light-gray">
            <DateDisplay date={currentMediaItem?.updated_at || ''} type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME} />
          </div>
          {currentSlide?.kind === 'single' && currentMediaItem && (
            <ClientTagChips
              item={currentMediaItem}
              editable={editableTagSide === 'client'}
              onToggle={(item, tag) => handleTagToggle(item, 'client', tag)}
            />
          )}
        </SlideInfoText>
        <MobileFooterActions>
          {renderActionIcons(40)}
        </MobileFooterActions>
      </MobileFooterRow>

      {/* コメントオーバーレイ（コメントアイコンで開閉） */}
      <MobileCommentOverlay
        $visible={showMobileComments}
        $background={projectAmbient?.background}
      >
        <MobileCommentHeader>
          <IconButton onClick={() => setShowMobileComments(false)} icon="chevron_left" />
          <span className="heading-spacing u-text-black">コメント</span>
          {commentCount > 0 && (
            <span className="bodytext-11" style={{ color: '#888' }}>{commentCount}件</span>
          )}
        </MobileCommentHeader>

        <MobileCommentBody>
          {/* フィルターバー（コメント一覧の上にフロート） */}
          <TopAppBarFloat>
            <TopAppBarHome
              options={commentFilterOptions}
              defaultValue={firstAvailableMode}
              value={filterMode}
              onChange={handleFilterModeChange}
              showSearchInput={true}
              onSearchChange={handleSearchTextChange}
              searchPlaceholder="コメントを検索..."
            />
          </TopAppBarFloat>

          <MobileCommentList ref={commentListScrollRef} $bottomPadding={commentListBottomPadding}>
            <LayoutGroup key={filterMode}>
              {filteredComments.map(renderCommentItem)}
            </LayoutGroup>
          </MobileCommentList>
        </MobileCommentBody>

        <MobileCommentFieldWrapper ref={commentFieldWrapperRef}>{renderCommentField()}</MobileCommentFieldWrapper>
      </MobileCommentOverlay>
    </MobileLayout>
    </DropZoneOverlay>
  );

  // ======================
  //  Main Render
  // ======================
  return createPortal(
    <ModalOverlay
      className={fadingOut ? 'fade-out-bck' : undefined}
      style={fadingOut ? { pointerEvents: 'none' } : undefined}
      $background={projectAmbient?.background}
    >
      {/* プロジェクトのバナー色に呼応するアンビエント背景（プロジェクト内表示のみ） */}
      {projectAmbient && (
        <ProjectAmbientBackground
          variant="container"
          accents={projectAmbient.accents}
        />
      )}
      {isDesktop ? renderDesktop() : renderMobile()}

      {/* アップロード進捗モーダル */}
      {uploadingFiles.length > 0 && (
        <Modal
          onClose={() => {
            if (!uploadingFiles.some(f => f.status === 'uploading' || f.status === 'pending')) {
              handleUploadClear();
            }
          }}
          size="default"
        >
          <UploadModalTitle>ファイルアップロード</UploadModalTitle>
          <UploadProgressBar
            files={uploadingFiles}
            onCancel={handleUploadCancel}
            onRetry={(file) => void retryFile(file.id)}
            compact={false}
            fitToParent
            postProcessLabel={postProcessLabel}
          />
        </Modal>
      )}

      {/* アップロード部分失敗ダイアログ */}
      {uploadFailureDialog}

      {/* 削除確認ダイアログ */}
      {commentToDelete && (
        <ConfirmModal
          message="このコメントを削除しますか？"
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}

      {/* MA 書き出しモーダル（テロップ付き動画 / ミックス動画 / WAV / タイムチャート CSV） */}
      {showMaExportModal && maSequence && (
        <MaExportModal
          sequence={maSequence}
          currentSegmentIndex={currentSlide?.kind === 'ma' ? currentSlide.segmentIndex : null}
          previewSelections={maPreviewOptionSelections}
          previewSwitchOrder={maPreviewOptionOrder}
          fileBaseName={maTimeChartBaseName}
          onClose={() => setShowMaExportModal(false)}
        />
      )}

      {/* ショット編集モーダル */}
      {showShotEditModal && projectId && shotId && (
        <ShotEditModal
          projectId={projectId}
          shotId={shotId}
          shotName={shotName}
          mediaItems={mediaItemsData?.data?.media_items || []}
          initialTagIds={shotDetailData?.data?.shot?.tag_ids ?? shotFromState?.tag_ids ?? []}
          maInfo={
            maState.maFileItem
              ? {
                  linkedItemIds: maSequence?.linkedItemIds ?? new Set<string>(),
                  activeMaFileId: maState.maFileItem.id,
                  warnings: maSequence?.warnings ?? [],
                  fetchError: maState.fetchError,
                }
              : null
          }
          onClose={() => setShowShotEditModal(false)}
          onSaved={() => {
            refetchMediaItems();
          }}
        />
      )}
    </ModalOverlay>,
    document.body,
  );
};

export default Preview;
